import { useEffect, useMemo, useRef, useState } from 'react';
import Markdown from './Markdown';
import './App.css';
import {
  LANGUAGES,
  STRINGS,
  POLICIES,
  GENDER_OPTIONS,
  LEG_LENGTH_OPTIONS,
  SHOULDER_WIDTH_OPTIONS,
  BODY_TYPE_OPTIONS,
  UNIT_SYSTEMS,
  detectLanguage,
  persistLanguage,
  detectUnits,
  persistUnits,
} from './i18n';
import { supabase, isSupabaseEnabled } from './supabaseClient';
import { getPersonalColorPalette } from './personalColorPalettes';
import { funnel } from './analytics';
import ICON_GLYPHS from './iconGlyphs';

// ---------- 단위 변환 ----------
const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;
const LB_PER_KG = 2.20462;

function cmToFtIn(cm) {
  const totalIn = cm / CM_PER_INCH;
  const ft = Math.floor(totalIn / INCHES_PER_FOOT);
  const inch = Math.round(totalIn - ft * INCHES_PER_FOOT);
  if (inch === INCHES_PER_FOOT) return { ft: ft + 1, in: 0 };
  return { ft, in: inch };
}

function ftInToCm(ft, inch) {
  return (Number(ft) || 0) * INCHES_PER_FOOT * CM_PER_INCH + (Number(inch) || 0) * CM_PER_INCH;
}

function kgToLb(kg) {
  return Number(kg) * LB_PER_KG;
}

function lbToKg(lb) {
  return Number(lb) / LB_PER_KG;
}

// 디자인 미리보기용 샘플 리포트 (?preview=result 일 때만 사용)
const SAMPLE_REPORT_KO = `## 종합 진단

당신의 실루엣은 **고전적이면서도 현대적인 균형**을 보여줍니다. 어깨와 골반의 비율이 안정적이며 다리 길이가 평균보다 약간 깁니다. 이 비율은 *수직 라인 강조 코디*에 매우 유리합니다.

> "당신은 미니멀한 실루엣과 절제된 컬러 팔레트가 잘 어울리는 타입입니다."

## 체형 분석 (Body Tech)

- **상체:** 어깨 라인 균형 / 약간의 V 라인
- **하체:** 다리 길이 우수 / 종아리 라인 슬림
- **추천 비율:** 상의 길이를 골반 아래 5cm 선에서 마무리

핵심 권장사항:
1. 허리선을 살릴 수 있는 **하이웨이스트 보텀**
2. 어깨 라인을 강조하는 **세트인 슬리브 재킷**
3. \`크롭 길이\` 대신 \`스탠더드 길이\`로 비율 보존

## 퍼스널 컬러 (Color Aura)

당신은 **딥 윈터 (Deep Winter)** 톤입니다. 차갑고 깊이 있는 컬러가 피부를 가장 또렷하게 보여줍니다.

**베스트 컬러:**
- 차콜 그레이
- 와인 레드
- 미드나잇 네이비
- 아이보리 화이트

**피해야 할 컬러:** 파스텔 옐로우, 살구색, 라이트 베이지 — 얼굴이 흐릿해 보일 수 있습니다.

## 추천 스타일 & 코디 팁

editorial한 무드의 **모던 클래식**이 가장 잘 어울립니다.

- 테일러드 코트 + 와이드 트라우저 + 첼시 부츠
- 캐시미어 니트 + 슬림 데님 + 로퍼
- 실크 셔츠 + A라인 스커트 + 미니멀 펌프스

## 피해야 할 스타일

너무 캐주얼한 *오버사이즈 후디*나 채도가 높은 *네온 컬러*는 당신의 정제된 분위기를 가립니다. 또한 과도한 패턴 믹스는 균형을 무너뜨립니다.

## 정리

당신의 스타일 DNA는 **"정제된 모던 에디토리얼"** 입니다. 절제된 라인, 깊이 있는 컬러, 고급스러운 소재 — 이 세 가지를 축으로 옷장을 큐레이팅하세요.`;

const MAX_FILE_BYTES = 50 * 1024 * 1024; // 원본 파일 안전 상한 (압축으로 최종 1.5MB 이하)
const MAX_IMAGE_DIMENSION = 1280;
const COMPRESS_TARGET_DATAURL_LENGTH = 2 * 1024 * 1024; // 약 1.5MB 바이너리

const PENDING_STORAGE_KEY = 'pending-style-analysis';
const PENDING_TTL_MS = 30 * 60 * 1000; // 30분 후 자동 만료

// localStorage 우선, 불가하면 sessionStorage 폴백.
// (인앱 브라우저·iOS Safari 등에서 결제 리디렉트 후 sessionStorage가 비는 케이스 대응)
function storages() {
  if (typeof window === 'undefined') return [];
  const out = [];
  try { if (window.localStorage) out.push(window.localStorage); } catch { /* ignore */ }
  try { if (window.sessionStorage) out.push(window.sessionStorage); } catch { /* ignore */ }
  return out;
}

function readPending() {
  for (const store of storages()) {
    try {
      const raw = store.getItem(PENDING_STORAGE_KEY);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== 'object') continue;
      // 신규 포맷 {savedAt, data} 우선, 구 포맷(평탄)도 호환
      if (obj.savedAt && obj.data) {
        if (Date.now() - obj.savedAt > PENDING_TTL_MS) {
          try { store.removeItem(PENDING_STORAGE_KEY); } catch { /* ignore */ }
          continue;
        }
        return obj.data;
      }
      return obj;
    } catch {
      /* try next storage */
    }
  }
  return null;
}

// 쓰기 실패 시 throw — 호출 측에서 결제 진행 중단 가능하도록.
function writePending(data) {
  const payload = JSON.stringify({ savedAt: Date.now(), data });
  let lastErr = null;
  let wrote = false;
  for (const store of storages()) {
    try {
      store.setItem(PENDING_STORAGE_KEY, payload);
      // 즉시 읽기로 검증 (사파리 프라이빗 모드 등 silent fail 방지)
      if (store.getItem(PENDING_STORAGE_KEY) === payload) {
        wrote = true;
      } else {
        lastErr = new Error('storage write verification failed');
      }
    } catch (err) {
      lastErr = err;
    }
  }
  if (!wrote) {
    throw lastErr || new Error('No storage available');
  }
}

function clearPending() {
  for (const store of storages()) {
    try { store.removeItem(PENDING_STORAGE_KEY); } catch { /* ignore */ }
  }
}

function stripCheckoutFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let changed = false;
  if (url.searchParams.has('checkout_id')) { url.searchParams.delete('checkout_id'); changed = true; }
  if (url.searchParams.has('pt')) { url.searchParams.delete('pt'); changed = true; }
  if (changed) {
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
}

// ──── 컬러 이름 → HEX 매핑 (긴 이름 먼저 매칭) ────
const COLOR_HEX = {
  // 한국어 — 복합 컬러 (긴 이름 우선)
  '미드나잇 네이비': '#1B2050',
  '아이보리 화이트': '#F6EFE0',
  '파스텔 옐로우': '#FDFD96',
  '파스텔 핑크': '#FFD1DC',
  '파스텔 블루': '#AEC6CF',
  '라이트 베이지': '#E8DAB2',
  '라이트 그레이': '#C7C7CC',
  '차콜 그레이': '#36454F',
  '와인 레드': '#722F37',
  '머스타드 옐로우': '#E1AD01',
  '올리브 그린': '#6B7A3A',
  '딥 그린': '#1E4D2B',
  '딥 블루': '#1B3A5B',
  '소프트 핑크': '#F4C2C2',
  '카키 그린': '#78866B',
  '브릭 레드': '#A2382A',
  // 한국어 — 단일
  '버건디': '#800020',
  '머스타드': '#D6A22A',
  '네이비': '#001F3F',
  '베이지': '#D2B48C',
  '브라운': '#654321',
  '카멜': '#C19A6B',
  '카키': '#78866B',
  '코랄': '#FF7F50',
  '라벤더': '#B57EDC',
  '민트': '#98D8C8',
  '실버': '#C0C0C0',
  '골드': '#D4AF37',
  '블랙': '#0A0A0A',
  '화이트': '#F8F8F8',
  '그레이': '#808080',
  '아이보리': '#F6EFE0',
  '살구색': '#FBCEB1',
  '살구': '#FBCEB1',
  '핑크': '#FFB6C1',
  '레드': '#D02828',
  '블루': '#1E40AF',
  '그린': '#228B22',
  '옐로우': '#F6C84C',
  '오렌지': '#FF8C42',
  '퍼플': '#7B4FA0',
  '바이올렛': '#7F00FF',
  '터쿼이즈': '#40E0D0',
  '에메랄드': '#0F5132',
  '루비': '#9B111E',
  '사파이어': '#0F52BA',
  '크림': '#FFFDD0',
  '청록': '#008B8B',
  '연두': '#A8C97E',
  '주황': '#FF8C42',
  '노랑': '#F6C84C',
  '보라': '#7B4FA0',
  '검정': '#0A0A0A',
  '하양': '#F8F8F8',
  '빨강': '#D02828',
  '파랑': '#1E40AF',
  '초록': '#228B22',
  // English — compound
  'Midnight Navy': '#1B2050',
  'Ivory White': '#F6EFE0',
  'Pastel Yellow': '#FDFD96',
  'Pastel Pink': '#FFD1DC',
  'Pastel Blue': '#AEC6CF',
  'Light Beige': '#E8DAB2',
  'Light Gray': '#C7C7CC',
  'Light Grey': '#C7C7CC',
  'Charcoal Gray': '#36454F',
  'Charcoal Grey': '#36454F',
  'Wine Red': '#722F37',
  'Mustard Yellow': '#E1AD01',
  'Olive Green': '#6B7A3A',
  'Deep Green': '#1E4D2B',
  'Deep Blue': '#1B3A5B',
  'Soft Pink': '#F4C2C2',
  'Khaki Green': '#78866B',
  'Brick Red': '#A2382A',
  // English — single
  'Burgundy': '#800020',
  'Mustard': '#D6A22A',
  'Navy': '#001F3F',
  'Beige': '#D2B48C',
  'Brown': '#654321',
  'Camel': '#C19A6B',
  'Khaki': '#78866B',
  'Coral': '#FF7F50',
  'Lavender': '#B57EDC',
  'Mint': '#98D8C8',
  'Silver': '#C0C0C0',
  'Gold': '#D4AF37',
  'Black': '#0A0A0A',
  'White': '#F8F8F8',
  'Gray': '#808080',
  'Grey': '#808080',
  'Ivory': '#F6EFE0',
  'Apricot': '#FBCEB1',
  'Pink': '#FFB6C1',
  'Red': '#D02828',
  'Blue': '#1E40AF',
  'Green': '#228B22',
  'Yellow': '#F6C84C',
  'Orange': '#FF8C42',
  'Purple': '#7B4FA0',
  'Violet': '#7F00FF',
  'Turquoise': '#40E0D0',
  'Emerald': '#0F5132',
  'Ruby': '#9B111E',
  'Sapphire': '#0F52BA',
  'Cream': '#FFFDD0',
  'Teal': '#008B8B',
};

const COLOR_NAMES_SORTED = Object.keys(COLOR_HEX).sort((a, b) => b.length - a.length);

function findExactColor(text) {
  const t = text.trim().replace(/[.,;:!?·•\-—]+$/, '').trim();
  if (COLOR_HEX[t]) return { name: t, hex: COLOR_HEX[t] };
  return null;
}

// 텍스트 안에서 가장 앞에 위치한 컬러 매칭 (가장 긴 이름 우선)
function findFirstColor(text) {
  let best = null;
  for (const name of COLOR_NAMES_SORTED) {
    const idx = text.indexOf(name);
    if (idx === -1) continue;
    if (best === null || idx < best.index || (idx === best.index && name.length > best.name.length)) {
      best = { name, hex: COLOR_HEX[name], index: idx };
    }
  }
  return best;
}

function nodeToText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join('');
  if (node.props && node.props.children !== undefined) return nodeToText(node.props.children);
  return '';
}

function emitColorEvent(name, hex) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('aura:color', { detail: { name, hex } }));
}

// 인라인 텍스트 노드를 순회하여 컬러 이름을 클릭 가능한 ColorChip 으로 교체
function injectInlineColors(children) {
  if (children == null) return children;
  const arr = Array.isArray(children) ? children : [children];
  const out = [];
  let key = 0;
  for (const child of arr) {
    if (typeof child !== 'string') { out.push(child); continue; }
    let remaining = child;
    while (remaining.length > 0) {
      const m = findFirstColor(remaining);
      if (!m) { out.push(remaining); break; }
      if (m.index > 0) out.push(remaining.slice(0, m.index));
      out.push(
        <button
          type="button"
          key={`cc-${key++}`}
          className="color-chip"
          onClick={() => emitColorEvent(m.name, m.hex)}
          aria-label={`${m.name} 색상 보기`}
        >
          <span className="color-swatch sm" style={{ background: m.hex }} aria-hidden="true" />
          {m.name}
        </button>
      );
      remaining = remaining.slice(m.index + m.name.length);
    }
  }
  return out;
}

const MARKDOWN_COMPONENTS = {
  li: ({ node, children, ...props }) => {
    const text = nodeToText(children);
    const color = findExactColor(text);
    if (color) {
      return (
        <li className="color-swatch-item" {...props}>
          <button
            type="button"
            className="color-swatch-trigger"
            onClick={() => emitColorEvent(color.name, color.hex)}
            aria-label={`${color.name} 색상 보기`}
          >
            <span className="color-swatch" style={{ background: color.hex }} aria-hidden="true" />
            <span className="color-swatch-label">{children}</span>
            <span className="color-swatch-hex" aria-hidden="true">{color.hex.toUpperCase()}</span>
          </button>
        </li>
      );
    }
    return <li {...props}>{children}</li>;
  },
  p: ({ node, children, ...props }) => (
    <p {...props}>{injectInlineColors(children)}</p>
  ),
};

function iconForSection(title) {
  const t = title || '';
  if (t.includes('체형') || /body/i.test(t)) return 'accessibility_new';
  if (t.includes('컬러') || t.includes('색') || /color/i.test(t)) return 'palette';
  if (t.includes('피해') || t.includes('금지') || /avoid|don'?t/i.test(t)) return 'do_not_disturb';
  if (t.includes('코디') || t.includes('팁') || /tip|coord/i.test(t)) return 'tips_and_updates';
  if (t.includes('스타일') || t.includes('아이템') || t.includes('추천') || /style|item|recommend/i.test(t)) return 'checkroom';
  if (t.includes('정리') || t.includes('요약') || /summary|wrap/i.test(t)) return 'summarize';
  return 'auto_awesome';
}

function parseReportSections(report) {
  if (!report) return { intro: '', sections: [] };
  const lines = report.split(/\r?\n/);
  const sections = [];
  let intro = [];
  let current = null;
  let seenH2 = false;

  for (const raw of lines) {
    const h1 = raw.match(/^#\s+(.+)$/);
    const h2 = raw.match(/^##\s+(.+)$/);
    if (h2 || h1) {
      if (current) sections.push(current);
      const title = (h2 ? h2[1] : h1[1]).trim();
      current = { title, body: '' };
      seenH2 = true;
      continue;
    }
    if (!seenH2) {
      if (/^---+$/.test(raw.trim())) continue;
      intro.push(raw);
    } else if (current) {
      if (/^---+$/.test(raw.trim())) continue;
      current.body += raw + '\n';
    }
  }
  if (current) sections.push(current);
  return {
    intro: intro.join('\n').trim(),
    sections: sections
      .map((s) => ({ ...s, body: s.body.trim() }))
      .filter((s) => s.title || s.body),
  };
}

function compressImageToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('파일을 읽을 수 없습니다.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('이미지를 불러올 수 없습니다. (HEIC/HEIF 형식이라면 JPG로 변환해 주세요)'));
      img.onload = () => {
        const baseScale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(img.width, img.height));
        const baseW = Math.round(img.width * baseScale);
        const baseH = Math.round(img.height * baseScale);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const dimScales = [1, 0.75, 0.5];
        const qualities = [0.85, 0.7, 0.55, 0.4];
        let lastDataUrl = null;

        for (const ds of dimScales) {
          const w = Math.max(1, Math.round(baseW * ds));
          const h = Math.max(1, Math.round(baseH * ds));
          canvas.width = w;
          canvas.height = h;
          ctx.drawImage(img, 0, 0, w, h);
          for (const q of qualities) {
            const dataUrl = canvas.toDataURL('image/jpeg', q);
            lastDataUrl = dataUrl;
            if (dataUrl.length <= COMPRESS_TARGET_DATAURL_LENGTH) {
              resolve(dataUrl);
              return;
            }
          }
        }
        resolve(lastDataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function SubscribeCard({ t, user, onNeedLogin, onError }) {
  const [busy, setBusy] = useState(false);

  const handleSubscribe = async () => {
    funnel.subscribeCtaClick(!!user);
    if (!user) {
      funnel.subscribeLoginRequired();
      onNeedLogin?.();
      return;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          userEmail: user.email,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || `Checkout failed (${res.status})`);
      }
      funnel.beginCheckoutSubscription();
      window.location.href = data.url;
    } catch (err) {
      setBusy(false);
      onError?.(err?.message || String(err));
    }
  };

  return (
    <section className="card sub-card">
      <div className="sub-badge">{t.subBadge}</div>
      <h2 className="sub-title">{t.subTitle}</h2>
      <p className="sub-subtitle">{t.subSubtitle}</p>

      <ul className="sub-bullets">
        <li><Icon name="check_circle" fill /> {t.subBullet1}</li>
        <li><Icon name="check_circle" fill /> {t.subBullet2}</li>
        <li><Icon name="check_circle" fill /> {t.subBullet3}</li>
      </ul>

      <div className="sub-price-row">
        <span className="sub-price-amount">{t.subPriceMonthly}</span>
        <span className="sub-price-unit">{t.subPriceUnit}</span>
        <span className="sub-price-trial">{t.subPriceTrialBadge}</span>
      </div>

      <button
        type="button"
        className="cta-btn"
        onClick={handleSubscribe}
        disabled={busy}
      >
        {busy ? (
          <><span className="spinner" /> {t.subCtaBusy}</>
        ) : (
          <>{t.subCta} <Icon name="arrow_forward" /></>
        )}
      </button>
      <ul className="sub-chips" aria-label={t.subChipsLabel}>
        <li><Icon name="check_circle" fill /> {t.subChip1}</li>
        <li><Icon name="check_circle" fill /> {t.subChip2}</li>
        <li><Icon name="check_circle" fill /> {t.subChip3}</li>
      </ul>
      <p className="sub-trust">{t.subTrustLine}</p>
    </section>
  );
}

// 부드러운 스크롤 — 히어로/쇼케이스 CTA 가 해당 섹션으로 이동.
function scrollToId(id) {
  if (typeof document === 'undefined') return;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function HeroSection({ t }) {
  return (
    // 히어로 사진은 CSS background(.hero) 로 깔고, 그 위에 가독성 스크림(.hero::before)과
    // 카피를 겹쳐 조화시킨다. H1 이 LCP 로 유지되도록 사진은 background 로만 둔다.
    <section className="hero" role="img" aria-label={t.heroVisualAlt}>
      <div className="hero-copy">
        <span className="hero-eyebrow"><Icon name="auto_awesome" fill /> {t.heroEyebrow}</span>
        <h1 className="hero-title">{t.heroTitle}</h1>
        <p className="hero-subtitle">{t.heroSubtitle}</p>
        <div className="hero-ctas">
          <button type="button" className="cta-btn" onClick={() => scrollToId('subscribe')}>
            {t.heroCtaSub} <Icon name="arrow_forward" />
          </button>
          <button type="button" className="btn-ghost hero-cta-ghost" onClick={() => scrollToId('analyze')}>
            {t.heroCtaReport}
          </button>
        </div>
        <span className="hero-badge"><Icon name="check_circle" fill /> {t.heroBadge}</span>
      </div>
    </section>
  );
}

function SubscriptionShowcase({ t }) {
  return (
    <section className="showcase showcase-sub" aria-label={t.subShowTitle}>
      <div className="showcase-visual">
        <div className="brief-mock">
          <div className="brief-mock-head">
            <span className="brief-mock-kicker">{t.subShowMockKicker}</span>
            <span className="brief-mock-date">{t.subShowMockDate}</span>
          </div>
          <div className="brief-mock-weather"><Icon name="wb_sunny" fill /> {t.subShowMockWeather}</div>
          <div className="brief-mock-image" role="img" aria-label={t.subShowMockImageAlt} />
          <p className="brief-mock-line">{t.subShowMockLine1}</p>
          <p className="brief-mock-line dim">{t.subShowMockLine2}</p>
        </div>
      </div>
      <div className="showcase-copy">
        <span className="showcase-tag">{t.subShowTag}</span>
        <h2 className="showcase-title">{t.subShowTitle}</h2>
        <p className="showcase-desc">{t.subShowDesc}</p>
        <ul className="showcase-points">
          <li><Icon name="check_circle" fill /> {t.subShowPoint1}</li>
          <li><Icon name="check_circle" fill /> {t.subShowPoint2}</li>
          <li><Icon name="check_circle" fill /> {t.subShowPoint3}</li>
        </ul>
        <button type="button" className="cta-btn cta-btn-compact" onClick={() => scrollToId('subscribe')}>
          {t.subShowCta} <Icon name="arrow_forward" />
        </button>
      </div>
    </section>
  );
}

function ReportShowcase({ t }) {
  return (
    <section className="showcase showcase-report" aria-label={t.repShowTitle}>
      <div className="showcase-copy">
        <span className="showcase-tag">{t.repShowTag}</span>
        <h2 className="showcase-title">{t.repShowTitle}</h2>
        <p className="showcase-desc">{t.repShowDesc}</p>
        <ul className="showcase-points">
          <li><Icon name="palette" fill /> {t.repShowPoint1}</li>
          <li><Icon name="content_cut" fill /> {t.repShowPoint2}</li>
          <li><Icon name="lock_open" fill /> {t.repShowPoint3}</li>
        </ul>
        <button type="button" className="cta-btn cta-btn-compact" onClick={() => scrollToId('analyze')}>
          {t.repShowCta} <Icon name="arrow_forward" />
        </button>
      </div>
      <div className="showcase-visual">
        <div className="report-mock">
          <span className="report-mock-kicker">{t.repShowMockKicker}</span>
          <div className="report-mock-row"><Icon name="palette" fill /> {t.repShowMockColor}</div>
          <div className="report-mock-row"><Icon name="accessibility_new" fill /> {t.repShowMockBody}</div>
          <span className="report-mock-hair-label">{t.repShowMockHair}</span>
          <div className="report-mock-hair" role="img" aria-label={t.repShowMockHairAlt} />
        </div>
      </div>
    </section>
  );
}

function PolicyModal({ policy, onClose, t }) {
  useEffect(() => {
    if (!policy) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [policy, onClose]);

  if (!policy) return null;
  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <h2>{policy.title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t.close}>
            <Icon name="close" />
          </button>
        </header>
        <div className="modal-body">
          <Markdown>{policy.body}</Markdown>
        </div>
      </div>
    </div>
  );
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function ColorModal({ color, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!color) return undefined;
    setCopied(false);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [color, onClose]);

  if (!color) return null;
  const rgb = hexToRgb(color.hex);
  const hexUpper = color.hex.toUpperCase();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(hexUpper);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal color-modal" onClick={(e) => e.stopPropagation()}>
        <div className="color-modal-preview" style={{ background: color.hex }}>
          <button
            type="button"
            className="modal-close color-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="color-modal-body">
          <span className="color-modal-label">Color</span>
          <h2 className="color-modal-name">{color.name}</h2>
          <dl className="color-modal-meta">
            <div className="color-modal-row">
              <dt>HEX</dt>
              <dd>
                <button type="button" className="copy-hex" onClick={handleCopy}>
                  {copied ? 'Copied!' : hexUpper}
                  <Icon name={copied ? 'check' : 'content_copy'} />
                </button>
              </dd>
            </div>
            {rgb && (
              <div className="color-modal-row">
                <dt>RGB</dt>
                <dd>{rgb.r}, {rgb.g}, {rgb.b}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

function AuthModal({ open, onClose, t }) {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { kind: 'error'|'info', text }

  useEffect(() => {
    if (!open) return undefined;
    setMode('login');
    setEmail('');
    setPassword('');
    setBusy(false);
    setMsg(null);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  if (!isSupabaseEnabled) {
    return (
      <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
          <header className="modal-head">
            <h2>{t.authLogin}</h2>
            <button type="button" className="modal-close" onClick={onClose} aria-label={t.close}>
              <Icon name="close" />
            </button>
          </header>
          <div className="modal-body">
            <p>Supabase 가 아직 설정되지 않았습니다 (REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY env vars 필요).</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        funnel.login('password');
        onClose();
      } else if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
          },
        });
        if (error) throw error;
        funnel.signUp('email');
        if (data.session) {
          setMsg({ kind: 'info', text: t.authSignupSuccessNoConfirm });
          setTimeout(onClose, 1200);
        } else {
          setMsg({ kind: 'info', text: t.authSignupSuccess });
        }
      } else if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        });
        if (error) throw error;
        setMsg({ kind: 'info', text: t.authForgotPasswordSent });
      }
    } catch (err) {
      setMsg({ kind: 'error', text: t.authError(err?.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      });
      if (error) throw error;
      // OAuth 는 페이지 전환되므로 onClose 불필요
    } catch (err) {
      setBusy(false);
      setMsg({ kind: 'error', text: t.authError(err?.message || err) });
    }
  };

  const title =
    mode === 'login' ? t.authModalLoginTitle :
    mode === 'signup' ? t.authModalSignupTitle :
    t.authForgotPassword;

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal auth-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t.close}>
            <Icon name="close" />
          </button>
        </header>
        <div className="modal-body">
          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="label" htmlFor="auth-email">{t.authEmail}</label>
            <input
              id="auth-email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={busy}
            />
            {mode !== 'forgot' && (
              <>
                <label className="label" htmlFor="auth-password" style={{ marginTop: 12 }}>
                  {t.authPassword}
                </label>
                <input
                  id="auth-password"
                  type="password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder={mode === 'signup' ? t.authPasswordPlaceholder : ''}
                  disabled={busy}
                />
              </>
            )}
            {msg && (
              <div className={`auth-msg auth-msg-${msg.kind}`}>{msg.text}</div>
            )}
            <button type="submit" className="cta-btn cta-btn-compact auth-submit" disabled={busy}>
              {busy ? <span className="spinner" /> : (
                mode === 'login' ? t.authSubmitLogin :
                mode === 'signup' ? t.authSubmitSignup :
                t.authForgotPassword
              )}
            </button>
          </form>

          {mode !== 'forgot' && (
            <>
              <div className="auth-divider"><span>{t.authOrDivider}</span></div>
              <button type="button" className="btn-tonal auth-google" onClick={handleGoogle} disabled={busy}>
                <GoogleLogo /> {t.authGoogle}
              </button>
            </>
          )}

          <div className="auth-switch">
            {mode === 'login' && (
              <>
                <button type="button" className="link" onClick={() => setMode('signup')}>
                  {t.authSwitchToSignup}
                </button>
                <button type="button" className="link" onClick={() => setMode('forgot')}>
                  {t.authForgotPassword}
                </button>
              </>
            )}
            {mode === 'signup' && (
              <button type="button" className="link" onClick={() => setMode('login')}>
                {t.authSwitchToLogin}
              </button>
            )}
            {mode === 'forgot' && (
              <button type="button" className="link" onClick={() => setMode('login')}>
                {t.authSwitchToLogin}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer({ onOpenPolicy, t }) {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <p>{t.footerCopy}</p>
        <nav className="site-footer-links">
          <button type="button" onClick={() => onOpenPolicy('terms')}>{t.linkTerms}</button>
          <button type="button" onClick={() => onOpenPolicy('refund')}>{t.linkRefund}</button>
          <button type="button" onClick={() => onOpenPolicy('privacy')}>{t.linkPrivacy}</button>
          <button type="button" onClick={() => onOpenPolicy('aup')}>{t.linkAUP}</button>
        </nav>
      </div>
    </footer>
  );
}

function Icon({ name, className = '', fill = false }) {
  return (
    <span className={`material-symbols-outlined${fill ? ' fill' : ''} ${className}`.trim()}>
      {ICON_GLYPHS[name] || name}
    </span>
  );
}

// 브라우저/OS 가 'Etc/GMT-9' 같은 POSIX 오프셋 표기를 돌려줄 때(예: Windows 시계가
// 명명된 'Asia/Seoul' 대신 수동 UTC+9 로 설정된 경우) 친숙한 IANA 이름으로 정규화.
// 사인이 반대인 POSIX 규칙상 Etc/GMT-9 == UTC+9 라서 발송 시각 계산엔 영향 없음(표시용).
function normalizeTimezone(tz) {
  if (!tz) return 'UTC';
  const map = {
    'Etc/GMT-9': 'Asia/Seoul',   // UTC+9 (DST 없음)
    'Etc/GMT-8': 'Asia/Shanghai',
    'Etc/GMT-7': 'Asia/Bangkok',
    'Etc/GMT-5': 'Asia/Karachi',
    'Etc/GMT': 'UTC',
  };
  return map[tz] || tz;
}

function browserTimezone() {
  try {
    return normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  } catch {
    return 'UTC';
  }
}

// 네이티브 앱(Expo WebView) 안에서 실행 중인지. 웹뷰에서는 <a download>·blob·
// navigator.share(files) 가 동작하지 않아 네이티브 브리지로 저장/공유를 위임한다.
function isInApp() {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

function postToNative(payload) {
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

// 도시명 ↔ 좌표 선택 위젯. OpenWeather Geocoding(/api/geocode)으로 검색하고,
// 'GPS 감지' 로 navigator.geolocation 좌표 → 역지오코딩한다.
// 선택 시 onChange({ city, lat, lon }) 를, GPS 감지 시 onDetectTimezone(tz) 도 호출.
function CityPicker({ t, lang, inputId, city, onChange, onDetectTimezone, disabled }) {
  const [query, setQuery] = useState(city || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const selectedRef = useRef(city || '');
  const initRef = useRef(false);

  // 부모가 비동기로 city 를 채워준 첫 시점에 한 번만 동기화 (검색 트리거 방지).
  useEffect(() => {
    if (!initRef.current && city) {
      setQuery(city);
      selectedRef.current = city;
      initRef.current = true;
    }
  }, [city]);

  // 디바운스 검색.
  useEffect(() => {
    const qq = query.trim();
    if (qq.length < 2 || qq === selectedRef.current) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const h = setTimeout(async () => {
      try {
        const r = await fetch(`/api/geocode?q=${encodeURIComponent(qq)}&lang=${lang}`);
        const d = await r.json();
        if (cancelled) return;
        setResults(Array.isArray(d?.results) ? d.results : []);
        setOpen(true);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(h); };
  }, [query, lang]);

  const pick = (r) => {
    setQuery(r.display);
    selectedRef.current = r.display;
    setResults([]);
    setOpen(false);
    setStatusMsg('');
    onChange?.({ city: r.display, lat: r.lat, lon: r.lon });
  };

  const detect = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatusMsg(t.cityDetectFail);
      return;
    }
    setDetecting(true);
    setStatusMsg('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(`/api/geocode?lat=${latitude}&lon=${longitude}&lang=${lang}`);
          const d = await r.json();
          const top = d?.results?.[0];
          const display = top?.display || (lang === 'ko' ? '현재 위치' : 'Current location');
          setQuery(display);
          selectedRef.current = display;
          setResults([]);
          setOpen(false);
          onChange?.({ city: display, lat: latitude, lon: longitude });
          onDetectTimezone?.(browserTimezone());
        } catch {
          setStatusMsg(t.cityDetectFail);
        } finally {
          setDetecting(false);
        }
      },
      (err) => {
        setDetecting(false);
        setStatusMsg(err && err.code === 1 ? t.cityDetectDenied : t.cityDetectFail);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  };

  const trimmed = query.trim();
  const showNoResults = open && !searching && trimmed.length >= 2
    && trimmed !== selectedRef.current && results.length === 0;

  return (
    <div style={{ position: 'relative' }}>
      <div className="mypage-inline-row">
        <input
          id={inputId}
          className="input"
          type="text"
          value={query}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            onChange?.({ city: v, lat: null, lon: null });
          }}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder={t.obCityPlaceholder}
          autoComplete="off"
          disabled={disabled}
        />
        <button type="button" className="btn-tonal" onClick={detect} disabled={disabled || detecting}>
          <Icon name="my_location" /> {detecting ? t.cityDetecting : t.cityDetectBtn}
        </button>
      </div>

      {open && results.length > 0 && (
        <ul
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: 4,
            listStyle: 'none', padding: 4, background: '#1a1a22',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
            maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {results.map((r, i) => (
            <li key={`${r.lat},${r.lon},${i}`}>
              <button
                type="button"
                onClick={() => pick(r)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                  background: 'transparent', border: 'none', color: '#f5f5f7',
                  cursor: 'pointer', borderRadius: 6, fontSize: 14,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {r.label || r.display}
              </button>
            </li>
          ))}
        </ul>
      )}

      {searching && <p className="mypage-hint" style={{ marginTop: 6 }}>{t.citySearching}</p>}
      {showNoResults && <p className="mypage-hint" style={{ marginTop: 6 }}>{t.cityNoResults}</p>}
      {statusMsg && <p className="mypage-hint" style={{ marginTop: 6, color: '#e6a0a0' }}>{statusMsg}</p>}
    </div>
  );
}

// 미리보기 brief 생성 중 애니메이션 로더 (헤어스타일 로더 스타일 재사용 + 메시지 순환).
function BriefGeneratingLoader({ t }) {
  const steps = Array.isArray(t.obPreviewSteps) ? t.obPreviewSteps : [];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (steps.length <= 1) return undefined;
    const h = setInterval(() => setI((p) => (p + 1) % steps.length), 2200);
    return () => clearInterval(h);
  }, [steps.length]);
  return (
    <div className="card hairstyle-card hairstyle-loading-card" style={{ marginTop: 12 }}>
      <div className="hairstyle-loading-body">
        <div className="hairstyle-loading-orb" aria-hidden="true">
          <span className="hairstyle-loading-orb-ring" />
          <span className="hairstyle-loading-orb-ring delayed" />
          <span className="hairstyle-loading-orb-core"><Icon name="auto_awesome" fill /></span>
        </div>
        <h3 className="hairstyle-loading-title">{t.obPreviewLoadingTitle}</h3>
        <p className="hairstyle-loading-subtitle">{steps[i] || t.obPreviewSending}</p>
        <div className="hairstyle-loading-bar" aria-hidden="true">
          <span className="hairstyle-loading-bar-fill" />
        </div>
      </div>
    </div>
  );
}

// 메일로 발송된 미리보기 brief 를 화면에 렌더 (날씨 헤더 + 착장 이미지 + 마크다운 본문).
function PreviewBriefResult({ t, result }) {
  const w = result?.weather || {};
  const hasRange = w.temp_min_c != null && w.temp_max_c != null && w.temp_min_c !== w.temp_max_c;
  const tempLine = w.temp_c != null
    ? (hasRange ? `${w.temp_min_c}°–${w.temp_max_c}°C` : `${Math.round(w.temp_c)}°C`)
    : '';
  const metaBits = [
    tempLine && w.description ? `${tempLine} · ${w.description}` : (tempLine || w.description || ''),
    result.city || '',
    w.pop_pct != null ? `${t.obPreviewRain} ${w.pop_pct}%` : '',
  ].filter(Boolean);
  return (
    <div className="card preview-brief-card">
      <div className="preview-brief-head">
        <div className="preview-brief-kicker">AURA DAILY</div>
        {result.dateLabel && <div className="preview-brief-date">{result.dateLabel}</div>}
        {metaBits.length > 0 && <div className="preview-brief-meta">{metaBits.join(' · ')}</div>}
      </div>
      {result.outfitImageDataUrl && (
        <img src={result.outfitImageDataUrl} alt={t.obPreviewImageAlt} className="preview-brief-image" />
      )}
      {result.brief && (
        <div className="preview-brief-body">
          <Markdown components={MARKDOWN_COMPONENTS}>{result.brief}</Markdown>
        </div>
      )}
    </div>
  );
}

// Google 브랜드 로고 (공식 색상 — Google Sign-In 브랜딩 가이드라인 준수)
function GoogleLogo({ className = '' }) {
  return (
    <svg
      className={`google-logo ${className}`.trim()}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.63Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.46-.8 5.95-2.17l-2.9-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.95v2.32A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.73a5.4 5.4 0 0 1 0-3.46V4.95H.95a9 9 0 0 0 0 8.1l3.02-2.32Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .95 4.95l3.02 2.32C4.68 5.15 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function LangSwitcher({ value, onChange }) {
  return (
    <div className="lang-switcher" role="group" aria-label="Language">
      {LANGUAGES.map((l) => (
        <button
          key={l.value}
          type="button"
          className={`lang-btn${value === l.value ? ' is-active' : ''}`}
          onClick={() => onChange(l.value)}
          aria-pressed={value === l.value}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

function AppBar({ onBack, t, lang, onLangChange, user, onOpenAuth, onLogout, onOpenMyPage }) {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);
  return (
    <header className="appbar">
      <div className="appbar-inner">
        <button
          type="button"
          className="appbar-btn"
          aria-label={t.back}
          onClick={onBack}
          style={{ visibility: onBack ? 'visible' : 'hidden' }}
        >
          <Icon name="arrow_back" />
        </button>
        <p className="appbar-title" role="banner" aria-label={t.appTitle}>AURA</p>
        <div className="appbar-right">
          <LangSwitcher value={lang} onChange={onLangChange} />
          {isSupabaseEnabled && (
            user ? (
              <div className="account-wrap" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="account-btn"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label={t.authAccountMenu}
                  aria-expanded={menuOpen}
                >
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt=""
                      className="account-avatar-img"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="account-avatar">{(user.email || '?')[0].toUpperCase()}</span>
                  )}
                </button>
                {menuOpen && (
                  <div className="account-menu" role="menu">
                    <div className="account-menu-email" title={user.email}>{user.email}</div>
                    {onOpenMyPage && (
                      <button
                        type="button"
                        className="account-menu-item"
                        onClick={() => { setMenuOpen(false); onOpenMyPage(); }}
                      >
                        <Icon name="person" /> {t.authMyPage}
                      </button>
                    )}
                    <button
                      type="button"
                      className="account-menu-item"
                      onClick={() => { setMenuOpen(false); onLogout(); }}
                    >
                      <Icon name="logout" /> {t.authLogout}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button type="button" className="login-btn" onClick={onOpenAuth}>
                {t.authLogin}
              </button>
            )
          )}
        </div>
      </div>
    </header>
  );
}

function PillGroup({ value, onChange, options, disabled, equal = false, ariaLabel }) {
  return (
    <div
      className={`pill-group${equal ? ' equal' : ''}`}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt.value;
        const optLabel = typeof opt === 'string' ? opt : opt.label;
        const active = value === optValue;
        return (
          <button
            key={optValue}
            type="button"
            role="radio"
            aria-checked={active}
            className={`pill-option${active ? ' is-active' : ''}`}
            onClick={() => onChange(optValue)}
            disabled={disabled}
          >
            {optLabel}
          </button>
        );
      })}
    </div>
  );
}

function LoadingView({ t }) {
  const [activeStep, setActiveStep] = useState(0);
  const [longRunning, setLongRunning] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveStep((s) => (s < t.loaderSteps.length - 1 ? s + 1 : s));
    }, 4500);
    const longTimer = setTimeout(() => setLongRunning(true), 60000);
    return () => { clearInterval(id); clearTimeout(longTimer); };
  }, [t.loaderSteps.length]);

  const progress = ((activeStep + 1) / t.loaderSteps.length) * 100;

  return (
    <main className="loading-view">
      <h1>{t.loadingTitle}</h1>
      <p>{t.loadingSubtitle}</p>

      <div className="loader-ring" aria-hidden="true">
        <div className="loader-ring-track" />
        <div className="loader-ring-spin" />
        <div className="loader-ring-core">
          <Icon name="auto_awesome" fill />
        </div>
      </div>

      <div className="loader-steps" role="status" aria-live="polite">
        {t.loaderSteps.map((label, idx) => {
          const state =
            idx < activeStep ? 'is-done' : idx === activeStep ? 'is-active' : '';
          return (
            <div key={label} className={`loader-step ${state}`}>
              <span className="loader-step-marker" aria-hidden="true">
                {idx < activeStep ? (
                  <Icon name="check" />
                ) : idx === activeStep ? (
                  <span className="loader-step-dot" />
                ) : (
                  <span className="loader-step-num">{idx + 1}</span>
                )}
              </span>
              <span className="loader-step-label">{label}</span>
            </div>
          );
        })}
        <div className="loader-progress" aria-hidden="true">
          <div className="loader-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <p className="loading-caption">{t.loadingCaption}</p>
      {longRunning && (
        <p className="loading-long-hint">
          예상보다 시간이 걸리고 있어요. 절대 페이지를 닫지 마세요 — AI 이미지 생성이 진행 중입니다.
        </p>
      )}
    </main>
  );
}

function ReportSection({ title, body, index, t }) {
  return (
    <article className="report-section">
      <header className="report-section-head">
        <span className="report-section-icon">
          <Icon name={iconForSection(title)} fill />
        </span>
        <div>
          <span className="report-section-index">{t.sectionPrefix} {String(index + 1).padStart(2, '0')}</span>
          <h3 className="report-section-title">{title}</h3>
        </div>
      </header>
      <div className="report-section-body">
        <Markdown components={MARKDOWN_COMPONENTS}>{body}</Markdown>
      </div>
    </article>
  );
}

function PaletteChips({ colors, avoid }) {
  return (
    <div className="pc-chip-row">
      {colors.map((c) => (
        <div key={c.id} className={avoid ? 'pc-chip pc-chip-avoid' : 'pc-chip'} title={c.hex}>
          <span className="pc-chip-swatch" style={{ background: c.hex }} aria-hidden="true" />
          <span className="pc-chip-label">{c.name}</span>
        </div>
      ))}
    </div>
  );
}

// Section 2 "퍼스널 컬러" — AI 본문(요약) + 대표 피부색 + 베스트/회피 컬러 카드를
// 하나의 섹션 카드로 통합 렌더. report 섹션 중 퍼스널 컬러 항목 자리에 끼워 넣는다.
function PersonalColorSection({ section, index, palette, t }) {
  return (
    <article className="report-section">
      <header className="report-section-head">
        <span className="report-section-icon">
          <Icon name="palette" fill />
        </span>
        <div>
          <span className="report-section-index">{t.sectionPrefix} {String(index + 1).padStart(2, '0')}</span>
          <h3 className="report-section-title">{section.title}</h3>
        </div>
      </header>
      <div className="report-section-body">
        <div className="pc-type-row">
          <div className="pc-skin">
            <span className="pc-skin-swatch" style={{ background: palette.skin_tone }} aria-hidden="true" />
            <span className="pc-skin-label">{t.pcSkinTone}</span>
          </div>
          <div className="pc-type-meta">
            <span className="pc-palette-type">{palette.name}</span>
            <span className="pc-palette-tone">{palette.tone}</span>
          </div>
        </div>
        {section.body && (
          <div className="pc-summary">
            <h4 className="pc-summary-title">{t.pcSummary}</h4>
            <Markdown components={MARKDOWN_COMPONENTS}>{section.body}</Markdown>
          </div>
        )}
        <div className="pc-palette-group">
          <h4 className="pc-palette-group-title">{t.pcPaletteBest}</h4>
          <PaletteChips colors={palette.recommended_colors} />
        </div>
        <div className="pc-palette-group">
          <h4 className="pc-palette-group-title pc-palette-group-avoid">{t.pcPaletteAvoid}</h4>
          <PaletteChips colors={palette.avoid_colors} avoid />
        </div>
      </div>
    </article>
  );
}

// 폴백: report 에 퍼스널 컬러 섹션이 없을 때만 독립 카드로 노출.
function PersonalColorPaletteSection({ palette, t }) {
  if (!palette) return null;
  return (
    <section className="card pc-palette">
      <h2 className="card-title">
        <Icon name="palette" /> {t.pcPaletteTitle}
      </h2>
      <div className="pc-palette-head">
        <span className="pc-skin-swatch" style={{ background: palette.skin_tone }} aria-hidden="true" />
        <span className="pc-palette-type">{palette.name}</span>
        <span className="pc-palette-tone">{palette.tone}</span>
      </div>
      <div className="pc-palette-group">
        <h3 className="pc-palette-group-title">{t.pcPaletteBest}</h3>
        <PaletteChips colors={palette.recommended_colors} />
      </div>
      <div className="pc-palette-group">
        <h3 className="pc-palette-group-title pc-palette-group-avoid">{t.pcPaletteAvoid}</h3>
        <PaletteChips colors={palette.avoid_colors} avoid />
      </div>
    </section>
  );
}

function ResultView({ report, personalColor, hairstyleImage, hairstyleError, hairstyleLoading, emailStatus, onReset, t, lang }) {
  const { intro, sections } = useMemo(() => parseReportSections(report), [report]);
  const palette = useMemo(
    () => (personalColor?.type ? getPersonalColorPalette(personalColor.type, lang === 'en' ? 'en' : 'ko') : null),
    [personalColor, lang],
  );
  const pcSectionIdx = useMemo(
    () => sections.findIndex((s) => /퍼스널\s*컬러|personal\s*color/i.test(s.title || '')),
    [sections],
  );
  const captureRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [shareMsg, setShareMsg] = useState('');

  const generateImageBlob = async () => {
    if (!captureRef.current) throw new Error('No capture target.');
    const node = captureRef.current;

    // 1. 내부 모든 이미지가 완전히 로드될 때까지 대기
    //    (헤어스타일 base64 이미지가 로드 중이면 컨테이너 높이가 작게 측정되어 잘림)
    const images = Array.from(node.querySelectorAll('img'));
    await Promise.all(
      images.map((img) =>
        img.complete && img.naturalHeight > 0
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
      ),
    );

    // 2. 캡처 중에는 backdrop-filter 와 반투명 카드를 솔리드 배경으로 전환.
    //    html-to-image 의 SVG foreignObject 렌더링이 backdrop-filter 를 지원 안 해서
    //    카드가 빈/흐릿한 사각형으로 캡처되는 문제를 회피.
    document.body.classList.add('is-capturing');
    // 레이아웃이 settle 되도록 두 프레임 대기
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      // 3. 실제 전체 높이/너비 측정 + padding 포함한 캔버스 크기 계산
      const padding = 24;
      const rect = node.getBoundingClientRect();
      const contentWidth = Math.ceil(Math.max(node.scrollWidth, rect.width));
      const contentHeight = Math.ceil(Math.max(node.scrollHeight, rect.height));
      const totalWidth = contentWidth + padding * 2;
      const totalHeight = contentHeight + padding * 2;

      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0b0b10', // 다크 테마 배경 매칭
        skipFonts: true, // Google Fonts CSSStyleSheet 접근 SecurityError 회피
        width: totalWidth,
        height: totalHeight,
        canvasWidth: totalWidth,
        canvasHeight: totalHeight,
        style: {
          padding: `${padding}px`,
          width: `${contentWidth}px`,
          height: `${contentHeight}px`,
          // 박스 모델: padding 이 totalWidth/height 안에 포함되도록
          boxSizing: 'content-box',
        },
      });
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      return { blob, dataUrl };
    } finally {
      document.body.classList.remove('is-capturing');
    }
  };

  const handleDownloadImage = async () => {
    setShareMsg('');
    setCapturing(true);
    try {
      const { dataUrl } = await generateImageBlob();
      // 네이티브 앱(WebView)에서는 <a download> 가 동작하지 않아 네이티브 저장으로 위임
      if (isInApp()) {
        postToNative({ type: 'save-image', dataUrl });
        setShareMsg(t.imgSavedToApp);
        return;
      }
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `style-report-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setShareMsg(t.imgSaveFail(err.message || err));
    } finally {
      setCapturing(false);
    }
  };

  const handleShare = async () => {
    setShareMsg('');
    setCapturing(true);
    try {
      const { blob, dataUrl } = await generateImageBlob();
      // 네이티브 앱에서는 navigator.share(files) 미지원 → 네이티브 공유 시트로 위임
      if (isInApp()) {
        postToNative({
          type: 'share-image',
          dataUrl,
          text: `${t.shareText} ${window.location.origin}`,
          url: window.location.origin,
        });
        return;
      }
      const file = new File([blob], 'style-report.png', { type: 'image/png' });
      const shareData = {
        title: t.shareTitle,
        text: t.shareText,
        url: window.location.origin,
      };

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(window.location.origin);
        setShareMsg(t.shareFailUnsupportedWithCopy);
      } else {
        setShareMsg(t.shareFailUnsupported);
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setShareMsg(t.shareFail(err.message || err));
      }
    } finally {
      setCapturing(false);
    }
  };

  // 헤어스타일 이미지 저장. 앱(WebView)에서는 네이티브 저장으로 위임, 웹에서는 다운로드.
  const handleSaveHairstyle = () => {
    if (!hairstyleImage) return;
    setShareMsg('');
    if (isInApp()) {
      postToNative({ type: 'save-image', dataUrl: hairstyleImage });
      setShareMsg(t.imgSavedToApp);
      return;
    }
    const link = document.createElement('a');
    link.href = hairstyleImage;
    link.download = 'hairstyle.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="page page-wide">
        <div className="status-chip">
          <Icon name="check_circle" fill /> {t.resultChip}
        </div>
        <h1 className="result-title">{t.resultTitle}</h1>
        <p className="result-subtitle">{t.resultSubtitle}</p>

        <div className="legal-banner">
          <Icon name="info" />
          <span>{t.resultLegal}</span>
        </div>

        {emailStatus?.ok && (
          <div className="legal-banner" style={{ background: '#eef0ff', borderColor: '#c7caff', color: '#2b2f8c' }}>
            <Icon name="mark_email_read" fill />
            <span>
              <strong>{t.emailSentStrong}</strong>{' '}
              {emailStatus.to ? <>({emailStatus.to})</> : null}
              {' '}{t.emailSentSuffix}
            </span>
          </div>
        )}
        {emailStatus && !emailStatus.ok && (
          <div className="legal-banner" style={{ background: '#fff3e6', borderColor: '#ffd29b', color: '#7a4400' }}>
            <Icon name="mail" />
            <span>
              {t.emailFailPrefix} ({emailStatus.error}){t.emailFailSuffix}
            </span>
          </div>
        )}

        <div ref={captureRef} className="capture-area">

        {hairstyleImage ? (
          <div className="card hairstyle-card">
            <div className="hairstyle-head">
              <h2>
                <Icon name="content_cut" /> {t.hairstyleHead}
              </h2>
              <button type="button" onClick={handleSaveHairstyle} className="hairstyle-download">
                <Icon name="download" /> {t.hairstyleDownload}
              </button>
            </div>
            <img
              src={hairstyleImage}
              alt={t.hairstyleAlt}
              className="hairstyle-image"
            />
          </div>
        ) : hairstyleLoading ? (
          <div className="card hairstyle-card hairstyle-loading-card">
            <div className="hairstyle-head">
              <h2>
                <Icon name="content_cut" /> {t.hairstyleHead}
              </h2>
            </div>
            <div className="hairstyle-loading-body">
              <div className="hairstyle-loading-orb" aria-hidden="true">
                <span className="hairstyle-loading-orb-ring" />
                <span className="hairstyle-loading-orb-ring delayed" />
                <span className="hairstyle-loading-orb-core">
                  <Icon name="auto_awesome" fill />
                </span>
              </div>
              <h3 className="hairstyle-loading-title">{t.hairstyleLoadingTitle}</h3>
              <p className="hairstyle-loading-subtitle">{t.hairstyleLoadingSubtitle}</p>
              <div className="hairstyle-loading-bar" aria-hidden="true">
                <span className="hairstyle-loading-bar-fill" />
              </div>
            </div>
          </div>
        ) : hairstyleError ? (
          <div className="alert">
            <Icon name="error" /> {t.hairstyleError(hairstyleError)}
          </div>
        ) : null}

        {intro && (
          <div className="card report-intro">
            <Markdown components={MARKDOWN_COMPONENTS}>{intro}</Markdown>
          </div>
        )}

        <div className="report-sections">
          {sections.length > 0 ? (
            sections.map((section, idx) =>
              idx === pcSectionIdx && palette ? (
                <PersonalColorSection
                  key={`${idx}-${section.title}`}
                  section={section}
                  index={idx}
                  palette={palette}
                  t={t}
                />
              ) : (
                <ReportSection
                  key={`${idx}-${section.title}`}
                  title={section.title}
                  body={section.body}
                  index={idx}
                  t={t}
                />
              ),
            )
          ) : (
            <div className="card">
              <Markdown components={MARKDOWN_COMPONENTS}>{report}</Markdown>
            </div>
          )}
        </div>

        {palette && pcSectionIdx < 0 && (
          <PersonalColorPaletteSection palette={palette} t={t} />
        )}

        </div>

        {shareMsg && (
          <div className="legal-banner" style={{ marginTop: 16 }}>
            <Icon name="info" />
            <span>{shareMsg}</span>
          </div>
        )}

        <div className="result-actions">
          <button
            type="button"
            className="cta-btn cta-btn-compact"
            onClick={handleDownloadImage}
            disabled={capturing}
          >
            {capturing ? (
              <><span className="spinner" /> {t.btnGenerating}</>
            ) : (
              <><Icon name="download" /> {t.btnSaveImage}</>
            )}
          </button>
          <button
            type="button"
            className="btn-tonal"
            onClick={handleShare}
            disabled={capturing}
          >
            <Icon name="share" /> {t.btnShare}
          </button>
          <button type="button" className="btn-ghost" onClick={onReset}>
            <Icon name="refresh" /> {t.btnRetry}
          </button>
          {hairstyleImage && (
            <button type="button" onClick={handleSaveHairstyle} className="btn-ghost">
              <Icon name="image" /> {t.btnSaveHairstyle}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function OnboardingView({ user, t, lang, onDone }) {
  const [step, setStep] = useState(1);
  const TOTAL = 4;

  // Step 1: city + delivery
  const [city, setCity] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [tz, setTz] = useState(browserTimezone());
  const [deliveryHour, setDeliveryHour] = useState(7);

  // Step 2: photo
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Step 3: body
  const [gender, setGender] = useState('');
  const [units, setUnits] = useState(detectUnits());
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weightLb, setWeightLb] = useState('');
  const [legLength, setLegLength] = useState('보통');
  const [shoulderWidth, setShoulderWidth] = useState('보통');
  const [bodyType, setBodyType] = useState('보통형');

  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewDone, setPreviewDone] = useState(false);
  const [previewMsg, setPreviewMsg] = useState(null);  // {kind, text}
  const [previewResult, setPreviewResult] = useState(null);  // {brief, weather, outfitImageDataUrl, ...}
  const previewFiredRef = useRef(false);

  // 즉시 미리보기 brief 발송 (step 4 진입 시 자동 호출).
  const handlePreview = async () => {
    if (previewBusy || previewFiredRef.current) return;
    previewFiredRef.current = true;
    funnel.previewBriefRequest();
    setPreviewBusy(true);
    setPreviewMsg(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/api/preview-brief', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      // 응답 본문이 JSON 이 아닐 수 있으니 안전하게 파싱.
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { /* empty */ }
      if (!res.ok) {
        const msg = data?.error
          || (res.status === 404 ? '미리보기 endpoint 를 찾을 수 없어요. 배포 직후라면 잠시 후 다시 시도해 주세요.' : `Preview failed (${res.status})`);
        throw new Error(msg);
      }
      funnel.previewBriefSuccess();
      setPreviewMsg({ kind: 'info', text: t.obPreviewSent });
      setPreviewResult(data?.brief ? data : null);
      setPreviewDone(true);
    } catch (err) {
      setPreviewMsg({ kind: 'error', text: t.obPreviewFail(err?.message || String(err)) });
      previewFiredRef.current = false;  // 재시도 가능하도록 reset
    } finally {
      setPreviewBusy(false);
    }
  };

  // 온보딩 진입 (퍼널) — 세션당 1회.
  useEffect(() => {
    funnel.onboardingStart();
  }, []);

  // step 4 진입 시 자동으로 미리보기 발송 (단 한 번).
  useEffect(() => {
    if (step === 4 && !previewFiredRef.current) {
      handlePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { setErrorMsg(t.errorFileType); return; }
    if (f.size > MAX_FILE_BYTES) { setErrorMsg(t.errorFileSize); return; }
    setErrorMsg('');
    setPhotoFile(f);
    const r = new FileReader();
    r.onload = () => setPhotoPreview(r.result);
    r.readAsDataURL(f);
  };

  const heightFinal = () => units === 'metric'
    ? Number(heightCm) || null
    : (heightFt || heightIn ? Math.round(ftInToCm(heightFt, heightIn) * 10) / 10 : null);
  const weightFinal = () => units === 'metric'
    ? Number(weightKg) || null
    : (weightLb ? Math.round(lbToKg(weightLb) * 10) / 10 : null);

  const validStep = () => {
    if (step === 1) return city.trim().length > 0 && deliveryHour != null;
    if (step === 2) return !!photoFile;
    if (step === 3) {
      const h = heightFinal();
      const w = weightFinal();
      return gender && h && w && legLength && shoulderWidth && bodyType;
    }
    return true;
  };

  const handleFinish = async () => {
    if (!supabase) { setErrorMsg('Supabase not configured.'); return; }
    setBusy(true);
    setErrorMsg('');
    try {
      // 1. 사진 압축 + 업로드
      const dataUrl = await compressImageToDataURL(photoFile);
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append('photo', blob, 'profile.jpg');

      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('not authenticated');

      const upRes = await fetch('/api/upload-photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const upData = await upRes.json();
      if (!upRes.ok || !upData?.key) {
        throw new Error(upData?.error || `Upload failed (${upRes.status})`);
      }

      // 좌표 미선택 시 도시명으로 지오코딩 시도 (날씨가 좌표 기반이라 정확도↑).
      let finalLat = lat;
      let finalLon = lon;
      if ((finalLat == null || finalLon == null) && city.trim()) {
        try {
          const gr = await fetch(`/api/geocode?q=${encodeURIComponent(city.trim())}&lang=${lang}`);
          const gd = await gr.json();
          const top = gd?.results?.[0];
          if (top) { finalLat = top.lat; finalLon = top.lon; }
        } catch { /* 실패해도 도시명만 저장 */ }
      }

      // 2. profiles 테이블 update
      const profile = {
        height_cm: heightFinal(),
        weight_kg: weightFinal(),
        gender,
        leg_length: legLength,
        shoulder_width: shoulderWidth,
        body_type: bodyType,
        units,
        photo_r2_key: upData.key,
        photo_uploaded_at: new Date().toISOString(),
        city: city.trim(),
        latitude: finalLat,
        longitude: finalLon,
        timezone: tz,
        delivery_hour: Number(deliveryHour),
        language: lang,
        onboarded_at: new Date().toISOString(),
      };
      const { error: upErr } = await supabase
        .from('profiles')
        .update(profile)
        .eq('id', user.id);
      if (upErr) throw upErr;

      funnel.onboardingStep(3, 'body');
      funnel.onboardingComplete();
      setStep(4);
    } catch (err) {
      setErrorMsg(t.obSaveFail(err?.message || String(err)));
    } finally {
      setBusy(false);
    }
  };

  const progressPct = Math.round((step / TOTAL) * 100);

  return (
    <div className="page" style={{ maxWidth: 560 }}>
      <div className="ob-header">
        <span className="ob-progress-label">{t.obProgress(step, TOTAL)}</span>
        <div className="ob-progress"><div className="ob-progress-fill" style={{ width: `${progressPct}%` }} /></div>
      </div>

      {step === 1 && (
        <section className="card">
          <h2 className="ob-title">{t.obStep1Title}</h2>
          <p className="ob-subtitle">{t.obStep1Subtitle}</p>

          <div className="field field-block">
            <label className="label" htmlFor="ob-city">{t.obLabelCity}</label>
            <CityPicker
              t={t} lang={lang} inputId="ob-city" city={city}
              onChange={({ city: c, lat: la, lon: lo }) => { setCity(c); setLat(la); setLon(lo); }}
              onDetectTimezone={(z) => setTz(z)}
              disabled={busy}
            />
            <p className="ob-hint">{t.cityHint}</p>
          </div>

          <div className="field field-block">
            <span className="label">{t.obLabelHour}</span>
            <PillGroup
              value={String(deliveryHour)}
              onChange={(v) => setDeliveryHour(Number(v))}
              options={['5','6','7','8','9','10'].map((h) => ({ value: h, label: `${h}:00` }))}
              equal
            />
            <p className="ob-hint">Timezone: {tz}</p>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="card">
          <h2 className="ob-title">{t.obStep2Title}</h2>
          <p className="ob-subtitle">{t.obStep2Subtitle}</p>
          {photoPreview ? (
            <div className="photo-preview">
              <img src={photoPreview} alt="" />
              <button type="button" className="photo-remove" onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}>
                <Icon name="close" /> {t.photoRemove}
              </button>
            </div>
          ) : (
            <label htmlFor="ob-photo" className="dropzone">
              <Icon name="cloud_upload" />
              <p className="dropzone-primary">{t.dropzoneClick}</p>
              <p className="dropzone-hint">{t.dropzoneHint}</p>
            </label>
          )}
          <input id="ob-photo" type="file" accept="image/*" onChange={handlePhoto} className="photo-input" />
        </section>
      )}

      {step === 3 && (
        <section className="card">
          <h2 className="ob-title">{t.obStep3Title}</h2>
          <p className="ob-subtitle">{t.obStep3Subtitle}</p>

          <div className="field field-block">
            <span className="label">{t.labelGender}</span>
            <PillGroup value={gender} onChange={setGender} options={GENDER_OPTIONS[lang]} equal />
          </div>

          <div className="field field-block">
            <span className="label">{t.labelUnits}</span>
            <PillGroup
              value={units}
              onChange={setUnits}
              options={UNIT_SYSTEMS.map((u) => ({
                value: u.value,
                label: u.value === 'metric' ? t.unitMetric : t.unitImperial,
              }))}
              equal
            />
          </div>

          {units === 'metric' ? (
            <div className="field-row field-block">
              <div className="field">
                <label className="label">{t.labelHeight}</label>
                <input className="input" type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} placeholder={t.placeholderHeight} />
              </div>
              <div className="field">
                <label className="label">{t.labelWeight}</label>
                <input className="input" type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder={t.placeholderWeight} />
              </div>
            </div>
          ) : (
            <div className="field-row field-block">
              <div className="field">
                <label className="label">{t.labelHeightFt}</label>
                <div className="imperial-height">
                  <input className="input" type="number" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} placeholder={t.placeholderFeet} />
                  <span className="input-suffix">{t.labelHeightFeet}</span>
                  <input className="input" type="number" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} placeholder={t.placeholderInches} />
                  <span className="input-suffix">{t.labelHeightInches}</span>
                </div>
              </div>
              <div className="field">
                <label className="label">{t.labelWeightLb}</label>
                <input className="input" type="number" value={weightLb} onChange={(e) => setWeightLb(e.target.value)} placeholder={t.placeholderLb} />
              </div>
            </div>
          )}

          <div className="field field-block">
            <span className="label">{t.labelLeg}</span>
            <PillGroup value={legLength} onChange={setLegLength} options={LEG_LENGTH_OPTIONS[lang]} />
          </div>
          <div className="field field-block">
            <span className="label">{t.labelShoulder}</span>
            <PillGroup value={shoulderWidth} onChange={setShoulderWidth} options={SHOULDER_WIDTH_OPTIONS[lang]} />
          </div>
          <div className="field field-block">
            <span className="label">{t.cardBody}</span>
            <PillGroup value={bodyType} onChange={setBodyType} options={BODY_TYPE_OPTIONS[lang]} />
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="card sub-card">
          <div className="sub-badge">✓ DONE</div>
          <h2 className="ob-title">{t.obStep4Title}</h2>
          <p className="ob-subtitle">
            {t.obStep4Subtitle.replace('{time}', `${String(deliveryHour).padStart(2,'0')}:00`)}
          </p>

          {/* 생성 중 애니메이션 → 완료 시 brief 화면 표시 */}
          {previewBusy && <BriefGeneratingLoader t={t} />}

          {previewMsg && !previewBusy && (
            <div className={`auth-msg auth-msg-${previewMsg.kind}`} style={{ marginBottom: 12 }}>{previewMsg.text}</div>
          )}

          {!previewBusy && previewResult && <PreviewBriefResult t={t} result={previewResult} />}

          {/* 실패 시에만 재시도 버튼 노출. 성공/대기 중에는 단순 닫기 버튼. */}
          {previewMsg?.kind === 'error' && !previewBusy ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn-tonal" onClick={handlePreview} disabled={previewBusy}>
                <Icon name="refresh" /> {t.obBtnPreviewRetry}
              </button>
              <button type="button" className="btn-ghost" onClick={onDone}>
                {t.obBtnClose}
              </button>
            </div>
          ) : (
            <button type="button" className="cta-btn" onClick={onDone} disabled={previewBusy}>
              {previewDone ? t.obBtnGoHome : t.obBtnClose}
              <Icon name="arrow_forward" />
            </button>
          )}
        </section>
      )}

      {errorMsg && (
        <div className="alert"><Icon name="error" /> {errorMsg}</div>
      )}

      {step < 4 && (
        <div className="ob-nav">
          {step > 1 && (
            <button type="button" className="btn-ghost" onClick={() => setStep(step - 1)} disabled={busy}>
              <Icon name="arrow_back" /> {t.obBtnPrev}
            </button>
          )}
          {step < 3 && (
            <button
              type="button"
              className="cta-btn cta-btn-compact"
              onClick={() => {
                funnel.onboardingStep(step, step === 1 ? 'location_delivery' : 'photo');
                setStep(step + 1);
              }}
              disabled={!validStep() || busy}
              style={{ marginLeft: 'auto' }}
            >
              {t.obBtnNext} <Icon name="arrow_forward" />
            </button>
          )}
          {step === 3 && (
            <button
              type="button"
              className="cta-btn cta-btn-compact"
              onClick={handleFinish}
              disabled={!validStep() || busy}
              style={{ marginLeft: 'auto' }}
            >
              {busy ? <><span className="spinner" /> {t.obSaving}</> : <>{t.obBtnFinish}</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PasswordRecoveryView({ user, t, onDone, onCancel }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setMsg(null);
    if (pw.length < 6) return setMsg({ kind: 'error', text: t.myPagePasswordTooShort });
    if (pw !== pw2) return setMsg({ kind: 'error', text: t.myPagePasswordMismatch });
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      onDone?.();
    } catch (err) {
      setMsg({ kind: 'error', text: t.authError(err?.message || err) });
      setBusy(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 480 }}>
      <div className="page-header">
        <h1>{t.pwRecoveryTitle}</h1>
        <p>{t.pwRecoverySubtitle}</p>
      </div>
      <section className="card">
        <form className="mypage-form" onSubmit={handleSubmit}>
          {user?.email && (
            <div className="mypage-info" style={{ marginBottom: 16 }}>
              <div>
                <dt>{t.myPageEmail}</dt>
                <dd>{user.email}</dd>
              </div>
            </div>
          )}
          <label className="label" htmlFor="pwr-pw1">{t.myPageNewPassword}</label>
          <input
            id="pwr-pw1"
            className="input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            minLength={6}
            placeholder={t.myPageNewPasswordPlaceholder}
            autoComplete="new-password"
            autoFocus
            disabled={busy}
          />
          <label className="label" htmlFor="pwr-pw2" style={{ marginTop: 12 }}>
            {t.myPageConfirmPassword}
          </label>
          <input
            id="pwr-pw2"
            className="input"
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            minLength={6}
            autoComplete="new-password"
            disabled={busy}
          />
          {msg && (
            <div className={`auth-msg auth-msg-${msg.kind}`}>{msg.text}</div>
          )}
          <button
            type="submit"
            className="cta-btn cta-btn-compact"
            style={{ marginTop: 16, width: '100%' }}
            disabled={busy || !pw || !pw2}
          >
            {busy ? <span className="spinner" /> : t.myPagePasswordSubmit}
          </button>
          <button
            type="button"
            className="btn-ghost"
            style={{ marginTop: 8, width: '100%' }}
            onClick={onCancel}
            disabled={busy}
          >
            {t.pwRecoveryCancel}
          </button>
        </form>
      </section>
    </div>
  );
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(onClose, toast.duration || 5000);
    return () => clearTimeout(id);
  }, [toast, onClose]);

  if (!toast) return null;
  return (
    <div className={`toast toast-${toast.kind || 'info'}`} role="status" aria-live="polite">
      <Icon name={toast.kind === 'success' ? 'check_circle' : 'info'} fill />
      <span className="toast-text">{toast.text}</span>
      <button type="button" className="toast-close" onClick={onClose} aria-label="Close">
        <Icon name="close" />
      </button>
    </div>
  );
}

function SubscriptionSection({ user, t, lang, onStartSubscribe, onToast }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    if (!supabase) return;
    setLoading(true);
    setErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/api/my-subscription', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Fetch failed (${res.status})`);
      setSub(data.subscription);
    } catch (e) {
      setErr(t.mySubActionFail(e?.message || String(e)));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const action = async (kind) => {
    if (kind === 'cancel') {
      // 트라이얼 중 취소 = 즉시 종료. 유료 = 기간 끝까지 사용. confirm 메시지 분기.
      const msg = sub?.status === 'trialing' ? t.mySubCancelConfirmTrial : t.mySubCancelConfirm;
      if (!window.confirm(msg)) return;
    }
    setBusy(true);
    setErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/api/subscription-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: kind, days: 7 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Action failed (${res.status})`);
      await refresh();
      onToast?.(t.mySubActionSaving.replace('...','') || 'done', 'success', 3000);
    } catch (e) {
      setErr(t.mySubActionFail(e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  };

  // Polar Customer Portal one-click URL → 새 탭에서 열기
  const [portalBusy, setPortalBusy] = useState(false);
  const openPortal = async () => {
    setPortalBusy(true);
    setErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/api/customer-portal-link', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.url) throw new Error(data?.error || `Portal link failed (${res.status})`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(t.mySubActionFail(e?.message || String(e)));
    } finally {
      setPortalBusy(false);
    }
  };

  const fmt = (iso) => iso
    ? new Date(iso).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US',
        { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  const statusLabel = (s) => ({
    trialing: t.mySubStatusTrialing,
    active: t.mySubStatusActive,
    past_due: t.mySubStatusPastDue,
    canceled: t.mySubStatusCanceled,
    paused: t.mySubStatusPaused,
  })[s] || s;

  const isActiveLike = sub && (sub.status === 'active' || sub.status === 'trialing');
  const isPaused = isActiveLike && sub.paused_until && new Date(sub.paused_until) > new Date();

  return (
    <section className="card">
      <h2 className="card-title">
        <Icon name="auto_awesome" fill /> {t.mySubTitle}
      </h2>

      {loading ? (
        <p className="mypage-hint">…</p>
      ) : !sub ? (
        <>
          <p className="mypage-hint">{t.mySubNone}</p>
          <button type="button" className="cta-btn cta-btn-compact" style={{ marginTop: 12 }} onClick={onStartSubscribe}>
            <Icon name="play_arrow" /> {t.mySubStartCta}
          </button>
        </>
      ) : (
        <>
          <dl className="mypage-info">
            <div>
              <dt>{t.mySubTitle}</dt>
              <dd>
                <span className={`sub-status sub-status-${sub.status}`}>{statusLabel(sub.status)}</span>
                {sub.cancel_at_period_end && (
                  <span className="sub-cancel-mark"> · {t.mySubStatusCanceled}</span>
                )}
              </dd>
            </div>
            {sub.status === 'trialing' && sub.trial_end && (
              <div>
                <dt>{t.mySubTrialEnds}</dt>
                <dd>{fmt(sub.trial_end)}</dd>
              </div>
            )}
            {sub.current_period_end && (
              <div>
                <dt>{t.mySubNextBilling}</dt>
                <dd>{fmt(sub.current_period_end)}</dd>
              </div>
            )}
            {isPaused && (
              <div>
                <dt>{t.mySubStatusPaused}</dt>
                <dd>{t.mySubPaused(fmt(sub.paused_until))}</dd>
              </div>
            )}
          </dl>

          {sub.cancel_at_period_end && (
            <p className="mypage-hint">{t.mySubCanceledNote}</p>
          )}

          {err && <div className="auth-msg auth-msg-error">{err}</div>}

          {isActiveLike && !sub.cancel_at_period_end && (
            <div className="mypage-form" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {isPaused ? (
                <button type="button" className="btn-tonal" onClick={() => action('resume')} disabled={busy}>
                  <Icon name="play_arrow" /> {t.mySubResumeCta}
                </button>
              ) : (
                <button type="button" className="btn-tonal" onClick={() => action('pause')} disabled={busy}>
                  <Icon name="pause" /> {t.mySubPauseCta}
                </button>
              )}
              <button type="button" className="btn-ghost" onClick={() => action('cancel')} disabled={busy}>
                <Icon name="block" /> {t.mySubCancelCta}
              </button>
            </div>
          )}

          {/* Polar Customer Portal — 결제수단·인보이스·해지 등 종합 관리 (구독이 있으면 항상 노출) */}
          <div className="mypage-form" style={{ marginTop: 12 }}>
            <button type="button" className="btn-tonal" onClick={openPortal} disabled={portalBusy}>
              {portalBusy
                ? <><span className="spinner" /> {t.mySubManageOpening}</>
                : <><Icon name="open_in_new" /> {t.mySubManageCta}</>
              }
            </button>
            <p className="mypage-hint" style={{ marginTop: 6 }}>{t.mySubManageHint}</p>
          </div>
        </>
      )}
    </section>
  );
}

function DeliveryPreferencesSection({ user, t, lang, onToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {kind:'info'|'error', text}

  // Read-only 참고값
  const [accountEmail, setAccountEmail] = useState('');  // 가입 이메일 (= profile.email)
  const [polarEmail, setPolarEmail] = useState(null);    // Polar 체크아웃 이메일

  // 편집 필드
  const [deliveryEmail, setDeliveryEmail] = useState('');
  const [city, setCity] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [timezone, setTimezone] = useState('');
  const [deliveryHour, setDeliveryHour] = useState(7);
  const [prefLang, setPrefLang] = useState(lang);

  // ── 초기 fetch ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase || !user?.id) return;
      try {
        // .maybeSingle() 사용 — profile 행이 없을 때 .single() 은 406 던짐.
        // handle_new_user 트리거가 실패한 케이스 등에서도 graceful degrade.
        const [{ data: p, error: pe }, { data: subs, error: se }] = await Promise.all([
          supabase.from('profiles')
            .select('email, delivery_email, city, latitude, longitude, timezone, delivery_hour, language, photo_r2_key')
            .eq('id', user.id)
            .maybeSingle(),
          supabase.from('subscriptions')
            .select('polar_customer_email,status,created_at')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(5),
        ]);
        if (cancelled) return;
        if (pe) throw pe;
        if (se) throw se;

        // 활성/체험 중 구독의 polar email 우선, 없으면 가장 최근 1건
        const activeSub = (subs || []).find((s) => s.status === 'active' || s.status === 'trialing');
        const refSub = activeSub || subs?.[0];
        setPolarEmail(refSub?.polar_customer_email || null);

        // 가입 이메일은 세션의 신선한 user.email 우선 (사용자가 이메일을 변경했을 수 있음)
        setAccountEmail(user.email || p?.email || '');
        setDeliveryEmail(p?.delivery_email || '');
        setCity(p?.city || '');
        setLat(p?.latitude ?? null);
        setLon(p?.longitude ?? null);
        setTimezone(normalizeTimezone(p?.timezone || browserTimezone()));
        setDeliveryHour(p?.delivery_hour ?? 7);
        setPrefLang(p?.language || lang);
      } catch (err) {
        if (!cancelled) setMsg({ kind: 'error', text: t.dpSaveFail(err?.message || String(err)) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const detectTz = () => {
    setTimezone(browserTimezone());
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setMsg(null);

    const trimmedEmail = deliveryEmail.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setMsg({ kind: 'error', text: t.dpEmailInvalid });
      return;
    }

    setSaving(true);
    try {
      // 좌표 미지정 시 도시명으로 지오코딩 시도.
      let finalLat = lat;
      let finalLon = lon;
      if ((finalLat == null || finalLon == null) && city.trim()) {
        try {
          const gr = await fetch(`/api/geocode?q=${encodeURIComponent(city.trim())}&lang=${prefLang}`);
          const gd = await gr.json();
          const top = gd?.results?.[0];
          if (top) { finalLat = top.lat; finalLon = top.lon; }
        } catch { /* 도시명만 저장 */ }
      }

      const payload = {
        delivery_email: trimmedEmail || null,
        city: city.trim() || null,
        latitude: finalLat,
        longitude: finalLon,
        timezone: timezone.trim() || null,
        delivery_hour: Number(deliveryHour),
        language: prefLang,
      };
      const { error } = await supabase
        .from('profiles')
        .update(payload)
        .eq('id', user.id);
      if (error) throw error;
      setMsg({ kind: 'info', text: t.dpSaved });
      onToast?.(t.dpSaved, 'success', 3000);
    } catch (err) {
      setMsg({ kind: 'error', text: t.dpSaveFail(err?.message || String(err)) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="card">
        <h2 className="card-title"><Icon name="mail" /> {t.dpSection}</h2>
        <p className="mypage-hint">…</p>
      </section>
    );
  }

  // 온보딩 미완료여도 발송 환경(이메일/도시/시간/언어)은 미리 설정 가능 — gating 제거.
  // 신체정보·사진은 별도의 ProfileSection 에서 관리.
  const trimmedDelivery = deliveryEmail.trim().toLowerCase();
  const accountLower = (accountEmail || '').toLowerCase();
  const polarLower = (polarEmail || '').toLowerCase();
  const differsFromAccount = !!trimmedDelivery && trimmedDelivery !== accountLower;
  const showPolarChip = !!polarEmail && polarLower !== accountLower;

  return (
    <section className="card">
      <h2 className="card-title">
        <Icon name="mail" /> {t.dpSection}
      </h2>
      <p className="mypage-hint" style={{ marginBottom: 16 }}>{t.dpHint}</p>

      <form className="mypage-form" onSubmit={handleSave}>
        {/* 받는 이메일 */}
        <label className="label" htmlFor="dp-email">{t.dpEmail}</label>
        <input
          id="dp-email"
          className="input"
          type="email"
          value={deliveryEmail}
          onChange={(e) => setDeliveryEmail(e.target.value)}
          placeholder={accountEmail || t.dpEmailPlaceholder}
          autoComplete="email"
          disabled={saving}
        />
        {(accountEmail || showPolarChip) && (
          <div className="dp-email-chips">
            {accountEmail && (
              <button
                type="button"
                className="chip-btn"
                onClick={() => setDeliveryEmail('')}
                disabled={saving}
                title={accountEmail}
              >{t.dpEmailUseAccount(accountEmail)}</button>
            )}
            {showPolarChip && (
              <button
                type="button"
                className="chip-btn"
                onClick={() => setDeliveryEmail(polarEmail)}
                disabled={saving}
                title={polarEmail}
              >{t.dpEmailUsePolar(polarEmail)}</button>
            )}
          </div>
        )}
        {differsFromAccount && (
          <div className="dp-warning">{t.dpEmailDiffersWarning}</div>
        )}

        {/* 도시 */}
        <label className="label" htmlFor="dp-city" style={{ marginTop: 18 }}>{t.dpCity}</label>
        <CityPicker
          t={t} lang={lang} inputId="dp-city" city={city}
          onChange={({ city: c, lat: la, lon: lo }) => { setCity(c); setLat(la); setLon(lo); }}
          onDetectTimezone={(z) => setTimezone(z)}
          disabled={saving}
        />
        <p className="mypage-hint" style={{ marginTop: 6 }}>{t.cityHint}</p>

        {/* 타임존 */}
        <label className="label" htmlFor="dp-tz" style={{ marginTop: 18 }}>{t.dpTimezone}</label>
        <div className="mypage-inline-row">
          <input
            id="dp-tz"
            className="input"
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="Asia/Seoul"
            disabled={saving}
          />
          <button type="button" className="btn-tonal" onClick={detectTz} disabled={saving}>
            <Icon name="my_location" /> {t.dpTimezoneAutoDetect}
          </button>
        </div>

        {/* 발송 시각 */}
        <label className="label" style={{ marginTop: 18 }}>{t.dpDeliveryHour}</label>
        <PillGroup
          value={String(deliveryHour)}
          onChange={(v) => setDeliveryHour(Number(v))}
          options={['5','6','7','8','9','10'].map((h) => ({ value: h, label: `${h}:00` }))}
        />
        <p className="mypage-hint" style={{ marginTop: 8 }}>
          {t.dpTimezoneHint(timezone || 'UTC', deliveryHour)}
        </p>

        {/* 언어 */}
        <label className="label" style={{ marginTop: 18 }}>{t.dpLanguage}</label>
        <PillGroup
          value={prefLang}
          onChange={(v) => setPrefLang(v)}
          options={[
            { value: 'ko', label: t.dpLanguageKo },
            { value: 'en', label: t.dpLanguageEn },
          ]}
        />

        {msg && (
          <div className={`auth-msg auth-msg-${msg.kind}`} style={{ marginTop: 12 }}>{msg.text}</div>
        )}

        <div style={{ marginTop: 18 }}>
          <button type="submit" className="cta-btn cta-btn-compact" disabled={saving}>
            {saving ? <><span className="spinner" /> {t.dpSaving}</> : t.dpSave}
          </button>
        </div>
      </form>
    </section>
  );
}

// 프로필 정보 편집 — 사진, 신체 정보. profiles 테이블에 저장. cron-daily-brief 가 이 값을 사용.
function ProfileSection({ user, t, lang, onToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // 현재 사진 메타 + 편집 직후 미리보기
  const [photoR2Key, setPhotoR2Key] = useState(null);
  const [photoUploadedAt, setPhotoUploadedAt] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null); // 방금 올린 새 사진 dataURL
  const fileInputRef = useRef(null);

  // 신체 정보
  const [gender, setGender] = useState('');
  const [units, setUnits] = useState('metric');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weightLb, setWeightLb] = useState('');
  const [legLength, setLegLength] = useState('');
  const [shoulderWidth, setShoulderWidth] = useState('');
  const [bodyType, setBodyType] = useState('');

  // ── 초기 fetch ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase || !user?.id) return;
      try {
        const { data: p, error: pe } = await supabase.from('profiles')
          .select('gender, units, height_cm, weight_kg, leg_length, shoulder_width, body_type, photo_r2_key, photo_uploaded_at')
          .eq('id', user.id)
          .maybeSingle();
        if (cancelled) return;
        if (pe) throw pe;
        const u = p?.units || 'metric';
        setUnits(u);
        setGender(p?.gender || '');
        setLegLength(p?.leg_length || '');
        setShoulderWidth(p?.shoulder_width || '');
        setBodyType(p?.body_type || '');
        setPhotoR2Key(p?.photo_r2_key || null);
        setPhotoUploadedAt(p?.photo_uploaded_at || null);
        if (p?.height_cm) {
          if (u === 'imperial') {
            const { ft, inch } = cmToFtIn(p.height_cm);
            setHeightFt(String(ft));
            setHeightIn(String(inch));
          } else {
            setHeightCm(String(p.height_cm));
          }
        }
        if (p?.weight_kg) {
          if (u === 'imperial') setWeightLb(String(Math.round(kgToLb(p.weight_kg))));
          else setWeightKg(String(p.weight_kg));
        }
      } catch (err) {
        if (!cancelled) setMsg({ kind: 'error', text: t.profileSaveFail(err?.message || String(err)) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const finalHeightCm = () => units === 'metric'
    ? Number(heightCm) || null
    : (heightFt || heightIn ? Math.round(ftInToCm(heightFt, heightIn) * 10) / 10 : null);
  const finalWeightKg = () => units === 'metric'
    ? Number(weightKg) || null
    : (weightLb ? Math.round(lbToKg(weightLb) * 10) / 10 : null);

  // 사진 변경 — 즉시 업로드 + profile.photo_r2_key 갱신
  const handlePhotoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setMsg({ kind: 'error', text: t.errorFileType });
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setMsg({ kind: 'error', text: t.errorFileSize });
      return;
    }
    setPhotoBusy(true);
    setMsg(null);
    try {
      const dataUrl = await compressImageToDataURL(f);
      setNewPhotoPreview(dataUrl);  // 사용자에게 즉시 새 사진 표시
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append('photo', blob, 'profile.jpg');
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const upRes = await fetch('/api/upload-photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const upData = await upRes.json();
      if (!upRes.ok || !upData?.key) throw new Error(upData?.error || `Upload failed (${upRes.status})`);
      const now = new Date().toISOString();
      const { error: upErr } = await supabase
        .from('profiles')
        .update({ photo_r2_key: upData.key, photo_uploaded_at: now })
        .eq('id', user.id);
      if (upErr) throw upErr;
      setPhotoR2Key(upData.key);
      setPhotoUploadedAt(now);
      onToast?.(t.profilePhotoUpdated, 'success', 3000);
    } catch (err) {
      setMsg({ kind: 'error', text: t.profileSaveFail(err?.message || String(err)) });
      setNewPhotoPreview(null);
    } finally {
      setPhotoBusy(false);
      // 파일 input 초기화해서 같은 파일 다시 선택 가능
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setMsg(null);
    setSaving(true);
    try {
      const h = finalHeightCm();
      const w = finalWeightKg();
      const payload = {
        gender: gender || null,
        units,
        height_cm: h,
        weight_kg: w,
        leg_length: legLength || null,
        shoulder_width: shoulderWidth || null,
        body_type: bodyType || null,
      };
      const { error } = await supabase.from('profiles').update(payload).eq('id', user.id);
      if (error) throw error;
      setMsg({ kind: 'info', text: t.profileSaved });
      onToast?.(t.profileSaved, 'success', 3000);
    } catch (err) {
      setMsg({ kind: 'error', text: t.profileSaveFail(err?.message || String(err)) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="card">
        <h2 className="card-title"><Icon name="person" /> {t.profileSection}</h2>
        <p className="mypage-hint">…</p>
      </section>
    );
  }

  const photoDate = photoUploadedAt
    ? new Date(photoUploadedAt).toLocaleDateString(lang === 'ko' ? 'ko-KR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  return (
    <section className="card">
      <h2 className="card-title"><Icon name="person" /> {t.profileSection}</h2>
      <p className="mypage-hint" style={{ marginBottom: 16 }}>{t.profileHint}</p>

      {/* 사진 */}
      <div className="field field-block">
        <span className="label">{t.profilePhoto}</span>
        <div className="profile-photo-row">
          <div className="profile-photo-preview">
            {newPhotoPreview ? (
              <img src={newPhotoPreview} alt="" />
            ) : photoR2Key ? (
              <div className="profile-photo-placeholder">
                <Icon name="check_circle" />
                <span>{photoDate ? t.profilePhotoUploaded(photoDate) : t.profilePhotoUploadedNoDate}</span>
              </div>
            ) : (
              <div className="profile-photo-placeholder profile-photo-empty">
                <Icon name="image" />
                <span>{t.profilePhotoNone}</span>
              </div>
            )}
          </div>
          <button type="button" className="btn-tonal" onClick={() => fileInputRef.current?.click()} disabled={photoBusy}>
            {photoBusy
              ? <><span className="spinner" /> {t.profilePhotoUploading}</>
              : <><Icon name="upload" /> {photoR2Key ? t.profilePhotoChange : t.profilePhotoUpload}</>
            }
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoPick}
          className="photo-input"
        />
      </div>

      {/* 신체 정보 폼 */}
      <form className="mypage-form" onSubmit={handleSave}>
        <div className="field field-block" style={{ marginTop: 18 }}>
          <span className="label">{t.labelGender}</span>
          <PillGroup value={gender} onChange={setGender} options={GENDER_OPTIONS[lang]} equal disabled={saving} />
        </div>

        <div className="field field-block">
          <span className="label">{t.labelUnits}</span>
          <PillGroup
            value={units}
            onChange={setUnits}
            options={UNIT_SYSTEMS.map((u) => ({
              value: u.value,
              label: u.value === 'metric' ? t.unitMetric : t.unitImperial,
            }))}
            equal
            disabled={saving}
          />
        </div>

        {units === 'metric' ? (
          <div className="field-row field-block">
            <div className="field">
              <label className="label">{t.labelHeight}</label>
              <input className="input" type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} placeholder={t.placeholderHeight} disabled={saving} />
            </div>
            <div className="field">
              <label className="label">{t.labelWeight}</label>
              <input className="input" type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder={t.placeholderWeight} disabled={saving} />
            </div>
          </div>
        ) : (
          <div className="field-row field-block">
            <div className="field">
              <label className="label">{t.labelHeightFt}</label>
              <div className="imperial-height">
                <input className="input" type="number" value={heightFt} onChange={(e) => setHeightFt(e.target.value)} placeholder={t.placeholderFeet} disabled={saving} />
                <span className="input-suffix">{t.labelHeightFeet}</span>
                <input className="input" type="number" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} placeholder={t.placeholderInches} disabled={saving} />
                <span className="input-suffix">{t.labelHeightInches}</span>
              </div>
            </div>
            <div className="field">
              <label className="label">{t.labelWeightLb}</label>
              <input className="input" type="number" value={weightLb} onChange={(e) => setWeightLb(e.target.value)} placeholder={t.placeholderLb} disabled={saving} />
            </div>
          </div>
        )}

        <div className="field field-block">
          <span className="label">{t.labelLeg}</span>
          <PillGroup value={legLength} onChange={setLegLength} options={LEG_LENGTH_OPTIONS[lang]} disabled={saving} />
        </div>
        <div className="field field-block">
          <span className="label">{t.labelShoulder}</span>
          <PillGroup value={shoulderWidth} onChange={setShoulderWidth} options={SHOULDER_WIDTH_OPTIONS[lang]} disabled={saving} />
        </div>
        <div className="field field-block">
          <span className="label">{t.cardBody}</span>
          <PillGroup value={bodyType} onChange={setBodyType} options={BODY_TYPE_OPTIONS[lang]} disabled={saving} />
        </div>

        {msg && (
          <div className={`auth-msg auth-msg-${msg.kind}`} style={{ marginTop: 12 }}>{msg.text}</div>
        )}

        <div style={{ marginTop: 18 }}>
          <button type="submit" className="cta-btn cta-btn-compact" disabled={saving}>
            {saving ? <><span className="spinner" /> {t.profileSaving}</> : t.profileSave}
          </button>
        </div>
      </form>
    </section>
  );
}

function MyPageView({ user, t, lang, onBack, onLogout, onDeleted, onStartSubscribe, onToast }) {
  // ── 로그인 방식 감지 ──
  const provider =
    user?.app_metadata?.provider ||
    user?.identities?.[0]?.provider ||
    'email';
  const isOAuth = provider !== 'email';

  // ── 닉네임 ──
  const [displayName, setDisplayName] = useState(
    user?.user_metadata?.display_name || user?.user_metadata?.full_name || '',
  );
  const [nameSaving, setNameSaving] = useState(false);
  const [nameMsg, setNameMsg] = useState(null); // {kind, text}

  const handleSaveName = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setNameSaving(true);
    setNameMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName.trim() },
      });
      if (error) throw error;
      setNameMsg({ kind: 'info', text: t.myPageNameSaved });
    } catch (err) {
      setNameMsg({ kind: 'error', text: t.authError(err?.message || err) });
    } finally {
      setNameSaving(false);
    }
  };

  // ── 비밀번호 변경 ──
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const handleChangePassword = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 6) return setPwMsg({ kind: 'error', text: t.myPagePasswordTooShort });
    if (newPw !== newPw2) return setPwMsg({ kind: 'error', text: t.myPagePasswordMismatch });
    setPwSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPw });
      if (error) throw error;
      setNewPw('');
      setNewPw2('');
      setPwMsg({ kind: 'info', text: t.myPagePasswordChanged });
    } catch (err) {
      setPwMsg({ kind: 'error', text: t.authError(err?.message || err) });
    } finally {
      setPwSaving(false);
    }
  };

  // ── 회원 탈퇴 ──
  const [delEmail, setDelEmail] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delMsg, setDelMsg] = useState(null);

  const handleDelete = async () => {
    setDelMsg(null);
    if (delEmail.trim().toLowerCase() !== (user?.email || '').toLowerCase()) {
      return setDelMsg({ kind: 'error', text: t.myPageDeleteEmailMismatch });
    }
    if (!window.confirm(t.myPageDeleteWarning)) return;
    setDelBusy(true);
    try {
      const { error } = await supabase.rpc('delete_my_account');
      if (error) throw error;
      // 삭제 직후 사용자가 더 이상 존재하지 않으므로 서버 logout 호출은 403.
      // 로컬 세션만 비워서 onAuthStateChange 가 user=null 로 떨어지게 한다.
      await supabase.auth.signOut({ scope: 'local' });
      onLogout?.();
      onDeleted?.(); // 부모에서 토스트 표시
      onBack();
    } catch (err) {
      setDelMsg({ kind: 'error', text: t.myPageDeleteFail(err?.message || err) });
      setDelBusy(false);
    }
  };

  const joinedDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(t.htmlLang === 'ko' ? 'ko-KR' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : '—';

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t.myPageTitle}</h1>
      </div>

      {/* 구독 (가장 자주 보는 정보 → 위) */}
      <SubscriptionSection
        user={user}
        t={t}
        lang={lang}
        onStartSubscribe={onStartSubscribe}
        onToast={onToast}
      />

      {/* 발송 환경설정 */}
      <DeliveryPreferencesSection
        user={user}
        t={t}
        lang={lang}
        onToast={onToast}
      />

      {/* 프로필 정보 (사진 + 신체정보) */}
      <ProfileSection
        user={user}
        t={t}
        lang={lang}
        onToast={onToast}
      />

      {/* 내 정보 */}
      <section className="card">
        <h2 className="card-title">
          <Icon name="account_circle" /> {t.myPageInfoSection}
        </h2>
        <dl className="mypage-info">
          <div>
            <dt>{t.myPageEmail}</dt>
            <dd>{user?.email || '—'}</dd>
          </div>
          <div>
            <dt>{t.myPageProvider}</dt>
            <dd>{isOAuth ? t.myPageProviderGoogle : t.myPageProviderEmail}</dd>
          </div>
          <div>
            <dt>{t.myPageJoined}</dt>
            <dd>{joinedDate}</dd>
          </div>
        </dl>

        <form className="mypage-form" onSubmit={handleSaveName}>
          <label className="label" htmlFor="mp-name">{t.myPageDisplayName}</label>
          <div className="mypage-inline-row">
            <input
              id="mp-name"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={40}
              placeholder={t.myPageDisplayNamePlaceholder}
              disabled={nameSaving}
            />
            <button type="submit" className="btn-tonal" disabled={nameSaving}>
              {nameSaving ? <span className="spinner" /> : t.myPageSaveName}
            </button>
          </div>
          {nameMsg && (
            <div className={`auth-msg auth-msg-${nameMsg.kind}`}>{nameMsg.text}</div>
          )}
        </form>
      </section>

      {/* 비밀번호 */}
      <section className="card">
        <h2 className="card-title">
          <Icon name="lock_reset" /> {t.myPagePasswordSection}
        </h2>
        {isOAuth ? (
          <p className="mypage-hint">{t.myPagePasswordNotForOAuth}</p>
        ) : (
          <form className="mypage-form" onSubmit={handleChangePassword}>
            <label className="label" htmlFor="mp-pw1">{t.myPageNewPassword}</label>
            <input
              id="mp-pw1"
              className="input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              minLength={6}
              placeholder={t.myPageNewPasswordPlaceholder}
              autoComplete="new-password"
              disabled={pwSaving}
            />
            <label className="label" htmlFor="mp-pw2" style={{ marginTop: 12 }}>
              {t.myPageConfirmPassword}
            </label>
            <input
              id="mp-pw2"
              className="input"
              type="password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              minLength={6}
              autoComplete="new-password"
              disabled={pwSaving}
            />
            {pwMsg && (
              <div className={`auth-msg auth-msg-${pwMsg.kind}`}>{pwMsg.text}</div>
            )}
            <button
              type="submit"
              className="cta-btn cta-btn-compact"
              style={{ marginTop: 16 }}
              disabled={pwSaving || !newPw || !newPw2}
            >
              {pwSaving ? <span className="spinner" /> : t.myPagePasswordSubmit}
            </button>
          </form>
        )}
      </section>

      {/* 탈퇴 */}
      <section className="card mypage-danger">
        <h2 className="card-title">
          <Icon name="delete_forever" /> {t.myPageDangerSection}
        </h2>
        <p className="mypage-hint mypage-danger-hint">{t.myPageDeleteWarning}</p>
        <label className="label" htmlFor="mp-del-email">{t.myPageDeleteConfirmLabel}</label>
        <input
          id="mp-del-email"
          className="input"
          type="email"
          value={delEmail}
          onChange={(e) => setDelEmail(e.target.value)}
          placeholder={user?.email || ''}
          disabled={delBusy}
        />
        {delMsg && (
          <div className={`auth-msg auth-msg-${delMsg.kind}`}>{delMsg.text}</div>
        )}
        <button
          type="button"
          className="btn-danger"
          style={{ marginTop: 16 }}
          onClick={handleDelete}
          disabled={delBusy || !delEmail}
        >
          {delBusy ? <><span className="spinner" /> {t.myPageDeleting}</> : <><Icon name="delete_forever" /> {t.myPageDeleteBtn}</>}
        </button>
      </section>
    </div>
  );
}

function App() {
  const [lang, setLang] = useState(detectLanguage);
  const t = STRINGS[lang];

  const [units, setUnits] = useState(detectUnits);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  // metric input
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  // imperial input
  const [heightFt, setHeightFt] = useState('');
  const [heightIn, setHeightIn] = useState('');
  const [weightLb, setWeightLb] = useState('');
  const [gender, setGender] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState('');
  const [personalColor, setPersonalColor] = useState(null); // { type } | null
  const [hairstyleImage, setHairstyleImage] = useState('');
  const [hairstyleError, setHairstyleError] = useState('');
  const [hairstyleLoading, setHairstyleLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ownPhotoConfirmed, setOwnPhotoConfirmed] = useState(false);
  const [activePolicy, setActivePolicy] = useState(null);
  const [activePolicyKey, setActivePolicyKey] = useState(null);
  const [activeColor, setActiveColor] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [user, setUser] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [myPageOpen, setMyPageOpen] = useState(false);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (text, kind = 'success', duration = 5000) =>
    setToast({ text, kind, duration });

  // 컬러 칩/스와치 클릭 → ColorModal 오픈
  useEffect(() => {
    const handler = (e) => setActiveColor(e.detail);
    window.addEventListener('aura:color', handler);
    return () => window.removeEventListener('aura:color', handler);
  }, []);

  // 홈(폼+구독카드) 최초 노출 — 퍼널 진입점. 다른 뷰가 아닐 때 1회 발화.
  const homeViewSeenRef = useRef(false);
  useEffect(() => {
    const inHome = !report && !loading && !onboardingOpen && !myPageOpen && !passwordRecovery;
    if (inHome && !homeViewSeenRef.current) {
      homeViewSeenRef.current = true;
      funnel.viewHome();
    }
  }, [report, loading, onboardingOpen, myPageOpen, passwordRecovery]);

  // Supabase 세션 구독 (로그인·로그아웃 자동 반영) + 비밀번호 재설정 링크 감지
  useEffect(() => {
    if (!isSupabaseEnabled) return undefined;
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      // OAuth(구글) 로그인은 리디렉트 복귀 후 SIGNED_IN 으로 도착.
      // password 는 handleSubmit 에서 이미 발화하므로 provider 가 email 이면 제외.
      if (event === 'SIGNED_IN') {
        const provider = session?.user?.app_metadata?.provider;
        if (provider && provider !== 'email') funnel.login(provider);
      }
      // 사용자가 이메일의 재설정 링크를 클릭하면 PASSWORD_RECOVERY 이벤트가 발생.
      // 세션은 이미 attach 되어 있어 updateUser 로 비밀번호만 갱신하면 됨.
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
      if (event === 'SIGNED_OUT') {
        setPasswordRecovery(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleLogout = async () => {
    if (!isSupabaseEnabled) return;
    await supabase.auth.signOut();
  };

  // 언어 변경 시 document.title, <html lang>도 갱신 + 열려 있는 정책 모달도 새 언어로 교체
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.title = t.docTitle;
      document.documentElement.setAttribute('lang', t.htmlLang);
    }
    if (activePolicyKey) {
      setActivePolicy(POLICIES[lang][activePolicyKey] || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const changeLang = (newLang) => {
    setLang(newLang);
    persistLanguage(newLang);
  };

  // 단위계 전환 — 기존 입력값이 있으면 새 단위로 변환해 보존
  const changeUnits = (newUnits) => {
    if (newUnits === units) return;
    if (newUnits === 'imperial') {
      if (heightCm) {
        const { ft, in: inch } = cmToFtIn(Number(heightCm));
        setHeightFt(String(ft));
        setHeightIn(String(inch));
      }
      if (weightKg) {
        setWeightLb(String(Math.round(kgToLb(weightKg))));
      }
    } else {
      if (heightFt || heightIn) {
        const cm = ftInToCm(heightFt, heightIn);
        setHeightCm(String(Math.round(cm)));
      }
      if (weightLb) {
        setWeightKg(String(Math.round(lbToKg(weightLb) * 10) / 10));
      }
    }
    setUnits(newUnits);
    persistUnits(newUnits);
  };

  // 백엔드/AI에 보낼 정규화된 미터법 값
  const canonicalHeight = () => {
    if (units === 'metric') return heightCm;
    if (!heightFt && !heightIn) return '';
    return String(Math.round(ftInToCm(heightFt, heightIn)));
  };
  const canonicalWeight = () => {
    if (units === 'metric') return weightKg;
    if (!weightLb) return '';
    return String(Math.round(lbToKg(weightLb) * 10) / 10);
  };

  const openPolicy = (key) => {
    setActivePolicyKey(key);
    setActivePolicy(POLICIES[lang][key] || null);
  };
  const closePolicy = () => {
    setActivePolicyKey(null);
    setActivePolicy(null);
  };

  // 결제 완료 후 자동 분석: ?checkout_id=... + localStorage 또는 KV(?pt=토큰) 복구
  // 디자인 미리보기: ?preview=result | ?preview=loading
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('preview');
    if (preview === 'result') {
      setReport(SAMPLE_REPORT_KO);
      return;
    }
    if (preview === 'loading') {
      setLoading(true);
      return;
    }
    // 구독 결제 완료 후 복귀 — checkout_id 와 함께 ?subscribed=1 플래그가 옴
    if (params.get('subscribed') === '1') {
      funnel.purchaseSubscription(params.get('checkout_id'));
      showToast(t.subSubscribedToast, 'success', 7000);
      const url = new URL(window.location.href);
      url.searchParams.delete('subscribed');
      url.searchParams.delete('checkout_id');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      // 결제 직후 onboarding 자동 시작 (로그인 상태일 때만 — 비로그인이면 다음 로그인시 검출)
      setOnboardingOpen(true);
      return;
    }
    const checkoutId = params.get('checkout_id');
    if (!checkoutId) return;
    // 클라이언트 storage가 있으면 fast path, 없으면 pendingToken으로 서버 측 KV 복구
    const pending = readPending();
    const pendingToken = params.get('pt');
    if (!pending && !pendingToken) {
      stripCheckoutFromUrl();
      setErrorMsg(t.errorCheckoutNoData);
      return;
    }
    runAnalysisFromPending(pending, checkoutId, pendingToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runAnalysisFromPending(pending, checkoutId, pendingToken) {
    setLoading(true);
    setReport('');
    setPersonalColor(null);
    setHairstyleImage('');
    setHairstyleError('');
    setHairstyleLoading(false);
    setEmailStatus(null);
    setErrorMsg('');
    const startedAt = Date.now();
    const tag = `[Aura ${checkoutId?.slice(-8) || 'x'}]`;
    console.log(tag, 'phase 1 (text) start', { hasLocalPending: !!pending, hasToken: !!pendingToken });

    const safe = pending || {};
    const requestLang = safe.lang || lang;
    if (safe.units && safe.units !== units) {
      setUnits(safe.units);
      persistUnits(safe.units);
    }

    // ── Phase 1: 텍스트 보고서 ──
    let reportText;
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 95000);
      try {
        const res = await fetch('/api/style-consult', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...safe, checkoutId, pendingToken, language: requestLang }),
          signal: ctrl.signal,
        });
        console.log(tag, 'phase 1 response', { status: res.status, elapsedMs: Date.now() - startedAt });
        const rawText = await res.text();
        let data = {};
        try { data = rawText ? JSON.parse(rawText) : {}; } catch { data = {}; }
        if (!res.ok) {
          console.error(tag, 'phase 1 error body', { status: res.status, data, rawText: data && Object.keys(data).length ? null : rawText.slice(0, 500) });
          if (data?.detail) console.error(tag, 'phase 1 OpenAI detail:', data.detail);
          if (data?.refund) console.error(tag, 'phase 1 refund:', data.refund);
          let msg;
          if (res.status === 524 || res.status === 504) {
            msg = `분석이 서버 시간 한계를 초과했습니다 (HTTP ${res.status}). 결제 금액은 환불 처리 중이니, 영업일 3~5일 내에 입금되지 않으면 xchanheex@gmail.com 으로 결제 ID ${checkoutId} 와 함께 문의 주세요.`;
          } else {
            msg = data?.error || t.errorRequest(res.status);
            if (data?.detail) msg += ` — ${data.detail.slice(0, 200)}`;
          }
          if (data?.refund?.ok) msg += t.refundAutoOk;
          else if (data?.refund && !data.refund.ok) msg += t.refundAutoFail(data.refund.error);
          throw new Error(msg);
        }
        reportText = data.report || t.errorReportEmpty;
        // v7 prompt 마커에서 추출된 8 타입 enum (없으면 null).
        setPersonalColor(data.personalColor || null);
      } finally {
        clearTimeout(timer);
      }
    } catch (err) {
      const elapsedMs = Date.now() - startedAt;
      console.error(tag, 'phase 1 failed', { err: err?.message, name: err?.name, elapsedMs });
      const isTimeout = err?.name === 'AbortError';
      setErrorMsg(isTimeout
        ? `텍스트 보고서 생성이 시간을 초과했습니다. 화면을 새로고침 하면 자동 재시도됩니다. (checkout: ${checkoutId})`
        : err?.message || t.errorUnknown);
      if (!isTimeout) {
        clearPending();
        stripCheckoutFromUrl();
      }
      setLoading(false);
      return;
    }

    // Phase 1 성공 → 결과 페이지 즉시 표시 (이미지는 로딩 카드로 placeholder)
    console.log(tag, 'phase 1 success', { reportLen: reportText.length });
    funnel.purchaseReport(checkoutId);
    setReport(reportText);
    setLoading(false);
    setHairstyleError('');      // 에러 영역은 비워둠
    setHairstyleLoading(true);  // 별도 로딩 카드 표시

    // ── Phase 2: 헤어스타일 이미지 ──
    console.log(tag, 'phase 2 (image) start');
    const phase2StartedAt = Date.now();
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 95000);
      try {
        const res = await fetch('/api/style-hairstyle', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            photoBase64: safe.photoBase64,
            checkoutId,
            pendingToken,
            language: requestLang,
            report: reportText,
          }),
          signal: ctrl.signal,
        });
        console.log(tag, 'phase 2 response', { status: res.status, elapsedMs: Date.now() - phase2StartedAt });
        const rawText = await res.text();
        let data = {};
        try { data = rawText ? JSON.parse(rawText) : {}; } catch { data = {}; }
        if (!res.ok) {
          console.error(tag, 'phase 2 error body', { status: res.status, data, rawText: data && Object.keys(data).length ? null : rawText.slice(0, 500) });
          let msg;
          if (res.status === 524 || res.status === 504) {
            msg = `헤어스타일 이미지 생성이 서버 시간 한계를 초과했습니다. 결제 환불 처리 중입니다 (checkout: ${checkoutId}).`;
          } else {
            msg = data?.error || `이미지 생성 실패 (${res.status})`;
            if (data?.detail) msg += ` — ${data.detail.slice(0, 200)}`;
          }
          if (data?.refund?.ok) msg += ' ✅ 환불 처리됨 (3~5 영업일).';
          else if (data?.refund && !data.refund.ok) msg += ` ⚠️ 자동 환불 실패: ${data.refund.error}`;
          throw new Error(msg);
        }
        console.log(tag, 'phase 2 success', { elapsedMs: Date.now() - phase2StartedAt });
        if (data.hairstyleImage) setHairstyleImage(data.hairstyleImage);
        if (data.email) setEmailStatus(data.email);
        setHairstyleError('');
        setHairstyleLoading(false);
        clearPending();
        stripCheckoutFromUrl();
      } finally {
        clearTimeout(timer);
      }
    } catch (err) {
      console.error(tag, 'phase 2 failed', { err: err?.message, name: err?.name });
      const isTimeout = err?.name === 'AbortError';
      setHairstyleLoading(false);  // 에러 시 로딩 카드 숨김
      setHairstyleError(isTimeout
        ? `헤어스타일 이미지 생성이 시간을 초과했습니다. 결제 환불 처리 중 (checkout: ${checkoutId}).`
        : err?.message || '헤어스타일 이미지 생성에 실패했습니다.');
      // 텍스트는 이미 표시됨, URL 정리하지 않으면 새로고침 시 phase 1을 다시 시도 → 위험
      clearPending();
      stripCheckoutFromUrl();
    }
  }

  const acceptFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMsg(t.errorFileType);
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setErrorMsg(t.errorFileSize);
      return;
    }
    setErrorMsg('');
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handlePhotoChange = (e) => acceptFile(e.target.files?.[0]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (loading) return;
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (loading) return;
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
  };

  const handleReset = () => {
    setReport('');
    setPersonalColor(null);
    setHairstyleImage('');
    setHairstyleError('');
    setHairstyleLoading(false);
    setEmailStatus(null);
    setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErrorMsg('');

    if (!photoFile) return setErrorMsg(t.errorPhoto);
    if (!gender) return setErrorMsg(t.errorGender);
    const heightMetric = canonicalHeight();
    const weightMetric = canonicalWeight();
    if (!heightMetric || !weightMetric) return setErrorMsg(t.errorHeightWeight);
    if (!ageConfirmed || !ownPhotoConfirmed) {
      return setErrorMsg(t.errorConsent);
    }

    funnel.beginCheckoutReport();
    setRedirecting(true);
    try {
      const photoBase64 = await compressImageToDataURL(photoFile);
      const pendingPayload = {
        photoBase64,
        height: heightMetric,
        weight: weightMetric,
        gender,
        lang, // 결제 후 돌아왔을 때 언어 유지
        units, // 결제 후 돌아왔을 때 단위 유지 (UX용, 백엔드는 항상 metric 사용)
      };

      // localStorage 저장은 best-effort fast path. 실패해도 결제 진행 — 백엔드 KV가 안전망.
      try { writePending(pendingPayload); } catch { /* fall back to server-side KV */ }

      // 백엔드 KV에 pending을 저장하고 토큰을 success_url에 포함시킨다.
      // → 인앱 브라우저 컨텍스트 전환 후에도 토큰만 있으면 복구 가능.
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pending: pendingPayload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        clearPending();
        throw new Error(data?.error || t.errorCheckoutCreate(res.status));
      }
      window.location.href = data.url;
    } catch (err) {
      setRedirecting(false);
      setErrorMsg(err.message || t.errorCheckoutRedirect);
    }
  };

  /* ---- PASSWORD RECOVERY VIEW (이메일 링크로 진입) ---- */
  if (passwordRecovery) {
    return (
      <>
        <AppBar t={t} lang={lang} onLangChange={changeLang} />
        <PasswordRecoveryView
          user={user}
          t={t}
          onDone={() => {
            setPasswordRecovery(false);
            showToast(t.pwRecoverySaved, 'success', 5000);
          }}
          onCancel={async () => {
            await supabase.auth.signOut();
            setPasswordRecovery(false);
          }}
        />
        <Footer onOpenPolicy={openPolicy} t={t} />
        <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  /* ---- ONBOARDING VIEW (구독 결제 직후) ---- */
  if (onboardingOpen && user) {
    return (
      <>
        <AppBar t={t} lang={lang} onLangChange={changeLang} user={user} onOpenAuth={() => setAuthOpen(true)} onLogout={handleLogout} onOpenMyPage={() => { setMyPageOpen(true); setOnboardingOpen(false); }} />
        <OnboardingView
          user={user}
          t={t}
          lang={lang}
          onDone={() => setOnboardingOpen(false)}
        />
        <Footer onOpenPolicy={openPolicy} t={t} />
        <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  /* ---- MY PAGE VIEW ---- */
  if (myPageOpen && user) {
    return (
      <>
        <AppBar onBack={() => setMyPageOpen(false)} t={t} lang={lang} onLangChange={changeLang} user={user} onOpenAuth={() => setAuthOpen(true)} onLogout={() => { handleLogout(); setMyPageOpen(false); }} onOpenMyPage={() => setMyPageOpen(true)} />
        <MyPageView
          user={user}
          t={t}
          lang={lang}
          onBack={() => setMyPageOpen(false)}
          onLogout={() => { handleLogout(); setMyPageOpen(false); }}
          onDeleted={() => showToast(t.myPageDeleteSuccess, 'success', 6000)}
          onStartSubscribe={() => { setMyPageOpen(false); /* main 으로 가면 SubscribeCard 보임 */ }}
          onToast={(text, kind, dur) => showToast(text, kind, dur)}
        />
        <Footer onOpenPolicy={openPolicy} t={t} />
        <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
        <ColorModal color={activeColor} onClose={() => setActiveColor(null)} />
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} t={t} />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  /* ---- LOADING VIEW ---- */
  if (loading) {
    return (
      <>
        <AppBar t={t} lang={lang} onLangChange={changeLang} user={user} onOpenAuth={() => setAuthOpen(true)} onLogout={handleLogout} onOpenMyPage={() => setMyPageOpen(true)} />
        <LoadingView t={t} />
        <Footer onOpenPolicy={openPolicy} t={t} />
        <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
        <ColorModal color={activeColor} onClose={() => setActiveColor(null)} />
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} t={t} />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  /* ---- RESULT VIEW ---- */
  if (report) {
    return (
      <>
        <AppBar onBack={handleReset} t={t} lang={lang} onLangChange={changeLang} user={user} onOpenAuth={() => setAuthOpen(true)} onLogout={handleLogout} onOpenMyPage={() => setMyPageOpen(true)} />
        <ResultView
          report={report}
          personalColor={personalColor}
          hairstyleImage={hairstyleImage}
          hairstyleError={hairstyleError}
          hairstyleLoading={hairstyleLoading}
          emailStatus={emailStatus}
          onReset={handleReset}
          t={t}
          lang={lang}
        />
        <Footer onOpenPolicy={openPolicy} t={t} />
        <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
        <ColorModal color={activeColor} onClose={() => setActiveColor(null)} />
        <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} t={t} />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </>
    );
  }

  /* ---- FORM VIEW ---- */
  return (
    <>
      <AppBar t={t} lang={lang} onLangChange={changeLang} user={user} onOpenAuth={() => setAuthOpen(true)} onLogout={handleLogout} onOpenMyPage={() => setMyPageOpen(true)} />
      <div className="page">
        <HeroSection t={t} />

        <SubscriptionShowcase t={t} />

        <div id="subscribe">
          <SubscribeCard
            t={t}
            user={user}
            onNeedLogin={() => { setErrorMsg(t.subErrorNeedLogin); setAuthOpen(true); }}
            onError={(msg) => setErrorMsg(msg)}
          />
        </div>

        <ReportShowcase t={t} />

        <section className="feature-grid" aria-label={t.featureLabel}>
          <div className="feature-item">
            <Icon name="bolt" fill />
            <h3>{t.feature1Title}</h3>
            <p>{t.feature1Desc}</p>
          </div>
          <div className="feature-item">
            <Icon name="shield_lock" fill />
            <h3>{t.feature2Title}</h3>
            <p>{t.feature2Desc}</p>
          </div>
          <div className="feature-item">
            <Icon name="psychology" fill />
            <h3>{t.feature3Title}</h3>
            <p>{t.feature3Desc}</p>
          </div>
          <div className="feature-item">
            <Icon name="verified" fill />
            <h3>{t.feature4Title}</h3>
            <p>{t.feature4Desc}</p>
          </div>
        </section>

        <section className="howto" id="howto" aria-label={t.howtoLabel}>
          <h2 className="section-title">{t.howtoTitle}</h2>
          <ol className="howto-steps">
            <li>
              <span className="howto-num">1</span>
              <div>
                <h3>{t.step1Title}</h3>
                <p>{t.step1Desc}</p>
              </div>
            </li>
            <li>
              <span className="howto-num">2</span>
              <div>
                <h3>{t.step2Title}</h3>
                <p>{t.step2Desc}</p>
              </div>
            </li>
            <li>
              <span className="howto-num">3</span>
              <div>
                <h3>{t.step3Title}</h3>
                <p>{t.step3Desc}</p>
              </div>
            </li>
          </ol>
        </section>

        <form id="analyze" onSubmit={handleSubmit}>
          <section className="card">
            <h2 className="card-title">
              <Icon name="photo_camera" /> {t.cardPhoto}
            </h2>
            {photoPreview ? (
              <div className="photo-preview">
                <img src={photoPreview} alt={t.cardPhoto} />
                <button
                  type="button"
                  className="photo-remove"
                  onClick={handleRemovePhoto}
                  disabled={loading}
                >
                  <Icon name="close" /> {t.photoRemove}
                </button>
              </div>
            ) : (
              <label
                className={`dropzone${isDragging ? ' is-dragging' : ''}`}
                htmlFor="photo-input"
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Icon name="cloud_upload" />
                <p className="dropzone-primary">
                  {isDragging ? t.dropzoneHere : t.dropzoneClick}
                </p>
                <p className="dropzone-hint">{t.dropzoneHint}</p>
              </label>
            )}
            <input
              id="photo-input"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="photo-input"
              disabled={loading}
            />
            <div className="dropzone-info">
              <Icon name="info" />
              <ul>
                <li>{t.photoTip1}</li>
                <li>{t.photoTip2Pre}<strong>{t.photoTip2Strong}</strong>{t.photoTip2Post}</li>
                <li>{t.photoTip3}</li>
                <li>{t.photoTip4}</li>
              </ul>
            </div>
          </section>

          <section className="card">
            <h2 className="card-title">
              <Icon name="badge" /> {t.cardBasic}
            </h2>
            <div className="field field-block">
              <span className="label">{t.labelGender}</span>
              <PillGroup
                value={gender}
                onChange={setGender}
                options={GENDER_OPTIONS[lang]}
                disabled={loading}
                equal
                ariaLabel={t.labelGender}
              />
            </div>
            <div className="field field-block">
              <span className="label">{t.labelUnits}</span>
              <PillGroup
                value={units}
                onChange={changeUnits}
                options={UNIT_SYSTEMS.map((u) => ({
                  value: u.value,
                  label: u.value === 'metric' ? t.unitMetric : t.unitImperial,
                }))}
                disabled={loading}
                equal
                ariaLabel={t.labelUnits}
              />
            </div>
            {units === 'metric' ? (
              <div className="field-row field-block">
                <div className="field">
                  <label className="label" htmlFor="height-input">{t.labelHeight}</label>
                  <input
                    id="height-input"
                    type="number"
                    inputMode="decimal"
                    min="50"
                    max="250"
                    step="0.1"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    placeholder={t.placeholderHeight}
                    className="input"
                    disabled={loading}
                  />
                </div>
                <div className="field">
                  <label className="label" htmlFor="weight-input">{t.labelWeight}</label>
                  <input
                    id="weight-input"
                    type="number"
                    inputMode="decimal"
                    min="20"
                    max="300"
                    step="0.1"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    placeholder={t.placeholderWeight}
                    className="input"
                    disabled={loading}
                  />
                </div>
              </div>
            ) : (
              <div className="field-row field-block">
                <div className="field">
                  <label className="label" htmlFor="height-ft-input">{t.labelHeightFt}</label>
                  <div className="imperial-height">
                    <input
                      id="height-ft-input"
                      type="number"
                      inputMode="numeric"
                      min="2"
                      max="8"
                      step="1"
                      value={heightFt}
                      onChange={(e) => setHeightFt(e.target.value)}
                      placeholder={t.placeholderFeet}
                      className="input"
                      disabled={loading}
                      aria-label={t.labelHeightFeet}
                    />
                    <span className="input-suffix">{t.labelHeightFeet}</span>
                    <input
                      id="height-in-input"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      max="11"
                      step="1"
                      value={heightIn}
                      onChange={(e) => setHeightIn(e.target.value)}
                      placeholder={t.placeholderInches}
                      className="input"
                      disabled={loading}
                      aria-label={t.labelHeightInches}
                    />
                    <span className="input-suffix">{t.labelHeightInches}</span>
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="weight-lb-input">{t.labelWeightLb}</label>
                  <input
                    id="weight-lb-input"
                    type="number"
                    inputMode="decimal"
                    min="50"
                    max="660"
                    step="1"
                    value={weightLb}
                    onChange={(e) => setWeightLb(e.target.value)}
                    placeholder={t.placeholderLb}
                    className="input"
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </section>

          <section className="consent-card">
            <h2 className="card-title">
              <Icon name="verified_user" /> {t.cardConsent}
            </h2>
            <label className="consent-item">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                disabled={loading}
              />
              <span>
                {t.consent1Pre}<strong>{t.consent1Strong}</strong>{t.consent1Mid}
                <button type="button" className="link" onClick={() => openPolicy('terms')}>
                  {t.consent1Terms}
                </button>
                {t.consent1And}
                <button type="button" className="link" onClick={() => openPolicy('privacy')}>
                  {t.consent1Privacy}
                </button>
                {t.consent1Post}
              </span>
            </label>
            <label className="consent-item">
              <input
                type="checkbox"
                checked={ownPhotoConfirmed}
                onChange={(e) => setOwnPhotoConfirmed(e.target.checked)}
                disabled={loading}
              />
              <span>
                {t.consent2Pre}<strong>{t.consent2Strong}</strong>{t.consent2Mid}
                <br />
                {t.consent2More}
                <button type="button" className="link" onClick={() => openPolicy('aup')}>
                  {t.consent2AUP}
                </button>
                {t.consent2Post}
              </span>
            </label>
          </section>

          <p className="legal-note">
            {t.legalNote1}<strong>{t.legalNote1Strong}</strong>{t.legalNote1Post}<br />
            <br />
            {t.legalNote2Pre}
            <button type="button" className="link" onClick={() => openPolicy('refund')}>
              {t.legalNote2Link}
            </button>
            {t.legalNote2Post}
          </p>

          {errorMsg && (
            <div className="alert">
              <Icon name="error" /> {errorMsg}
            </div>
          )}
        </form>

        <section className="faq" id="faq" aria-label={t.faqLabel}>
          <h2 className="section-title">{t.faqTitle}</h2>
          <div className="faq-list">
            <details>
              <summary>{t.faqQ1}</summary>
              <p>{t.faqA1}</p>
            </details>
            <details>
              <summary>{t.faqQ2}</summary>
              <p>{t.faqA2}</p>
            </details>
            <details>
              <summary>{t.faqQ3}</summary>
              <p>{t.faqA3}</p>
            </details>
            <details>
              <summary>{t.faqQ4}</summary>
              <p>{t.faqA4}</p>
            </details>
            <details>
              <summary>{t.faqQ5}</summary>
              <p>{t.faqA5}</p>
            </details>
            <details>
              <summary>{t.faqQ6}</summary>
              <p>{t.faqA6}</p>
            </details>
            <details>
              <summary>{t.faqQ7}</summary>
              <p>{t.faqA7}</p>
            </details>
            <details>
              <summary>{t.faqQ8}</summary>
              <p>{t.faqA8}</p>
            </details>
          </div>
        </section>
      </div>

      <div className="cta-bar">
        <div className="cta-bar-inner">
          <button
            type="button"
            className="cta-btn"
            onClick={handleSubmit}
            disabled={loading || redirecting}
          >
            {redirecting ? (
              <>
                <span className="spinner" /> {t.ctaRedirecting}
              </>
            ) : (
              <>
                {t.ctaButton} <Icon name="lock" fill />
              </>
            )}
          </button>
        </div>
      </div>

      <Footer onOpenPolicy={openPolicy} t={t} />
      <PolicyModal policy={activePolicy} onClose={closePolicy} t={t} />
      <ColorModal color={activeColor} onClose={() => setActiveColor(null)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} t={t} />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}

export default App;
